import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../Firebase';
import { defaultDepositSchemes } from '../Deposits';
import { defaultLoanProducts } from '../Loans';
import {
  mockCareers, mockNotices, mockCareerApplications, mockNews,
  defaultChairmanData, defaultCeoData, mockBoardOfDirectors
} from '../../utils/seedData';
import {
  LayoutDashboard, Landmark, LogOut, PlusCircle, Trash2, Edit, CheckCircle,
  AlertCircle, Sparkles, User, Users, Briefcase, MapPin, X, Loader,
  CreditCard, FileText, Eye, Phone, Mail, Calendar, IndianRupee, ClipboardList,
  Bell, Megaphone, UploadCloud, Download, GraduationCap, Award, Clock, UserCheck
} from 'lucide-react';

export default function AdminDashboard() {
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [schemes, setSchemes] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [depositEnquiries, setDepositEnquiries] = useState([]);
  const [loanEnquiries, setLoanEnquiries] = useState([]);
  const [contactEnquiries, setContactEnquiries] = useState([]);
  const [contactComplaints, setContactComplaints] = useState([]);
  const [contactFeedbacks, setContactFeedbacks] = useState([]);
  const [contactActiveTab, setContactActiveTab] = useState('enquiry');
  const [accountEnquiries, setAccountEnquiries] = useState([]);
  const [accountActiveTab, setAccountActiveTab] = useState('savings');
  
  // ========== CAREERS STATE ==========
  const [careersList, setCareersList] = useState([]);
  const [careerApplications, setCareerApplications] = useState([]);
  const [careersActiveTab, setCareersActiveTab] = useState('jobs'); // 'jobs' or 'applications'
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [jobFormMode, setJobFormMode] = useState('add'); // 'add' or 'edit'
  const [jobEditingId, setJobEditingId] = useState('');
  const [jobFormData, setJobFormData] = useState({
    id: '',
    title: '',
    department: 'Branch Operations',
    skills: '',
    qualification: '',
    vacancies: '',
    location: '',
    experience: '',
    type: 'Full-Time',
    salary: '',
    description: ''
  });
  const [viewingResumeModal, setViewingResumeModal] = useState(null);

  // ========== MANAGE NOTICES STATE ==========
  const [newsList, setNewsList] = useState([]);
  const [pdfNoticesList, setPdfNoticesList] = useState([]);
  const [noticesActiveTab, setNoticesActiveTab] = useState('news'); // 'news' or 'pdfNotices'
  const [noticeModalOpen, setNoticeModalOpen] = useState(false);
  const [noticeFormToggle, setNoticeFormToggle] = useState('category'); // 'category' or 'pdf'
  
  // Category News Form State
  const [categoryFormMode, setCategoryFormMode] = useState('add');
  const [categoryEditingId, setCategoryEditingId] = useState('');
  const [categoryFormData, setCategoryFormData] = useState({
    id: '',
    title: '',
    category: 'Announcements',
    date: new Date().toISOString().split('T')[0],
    summary: '',
    content: '',
    isCircular: false
  });

  // PDF Notice Form State
  const [pdfNoticeFormMode, setPdfNoticeFormMode] = useState('add');
  const [pdfNoticeEditingId, setPdfNoticeEditingId] = useState('');
  const [pdfNoticeFormData, setPdfNoticeFormData] = useState({
    id: '',
    title: '',
    noticeNo: '',
    date: new Date().toISOString().split('T')[0],
    department: 'Administration',
    description: '',
    pdfUrl: '',
    pdfName: '',
    fileSize: ''
  });
  const [viewingNoticeModal, setViewingNoticeModal] = useState(null);

  // ========== MANAGE MEMBERS / LEADERSHIP STATE ==========
  const [membersActiveTab, setMembersActiveTab] = useState('leadership'); // 'leadership' or 'directors'
  const [chairmanData, setChairmanData] = useState(defaultChairmanData);
  const [ceoData, setCeoData] = useState(defaultCeoData);
  const [directorsList, setDirectorsList] = useState([]);
  const [directorModalOpen, setDirectorModalOpen] = useState(false);
  const [directorFormMode, setDirectorFormMode] = useState('add'); // 'add' or 'edit'
  const [directorEditingId, setDirectorEditingId] = useState('');
  const [directorFormData, setDirectorFormData] = useState({
    id: '',
    name: '',
    role: 'Director',
    description: '',
    imageUrl: '',
    order: 1
  });

  // General & Enquiry View State
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [viewingEnquiry, setViewingEnquiry] = useState(null);

  // Stats Counters
  const [stats, setStats] = useState({
    schemesCount: 0,
    loansCount: 0,
    branchesCount: 0,
    depositEnquiriesCount: 0,
    loanEnquiriesCount: 0,
    adminsCount: 0,
    careersCount: 0,
    applicationsCount: 0,
    noticesCount: 0,
    directorsCount: 0
  });

  // ========== DEPOSIT SCHEME FORM STATE ==========
  const [formMode, setFormMode] = useState('add');
  const [editingId, setEditingId] = useState('');
  const [formData, setFormData] = useState({
    id: '', name: '', tagline: '', rate: '', numericRate: 6.00, tenure: '',
    minDeposit: '₹1,000', numericMin: 1000, maxDeposit: 'No Limit',
    features: '', benefits: '', overview: '', eligibility: '', imageUrl: '',
    documents: '', interestPayouts: '', prematureWithdrawal: '', loanMargin: '', nominationFacility: '', terms: ''
  });
  const [faqs, setFaqs] = useState([{ q: '', a: '' }]);
  const [brochures, setBrochures] = useState([{ title: '', desc: '', pdfUrl: '', pdfName: '' }]);

  // ========== LOAN PRODUCT FORM STATE ==========
  const [loanFormMode, setLoanFormMode] = useState('add');
  const [loanEditingId, setLoanEditingId] = useState('');
  const [loanFormData, setLoanFormData] = useState({
    id: '', name: '', tagline: '', rate: '', numericRate: 10.50,
    maxAmount: '', numericMin: 50000, numericMax: 500000, defaultTenure: 36,
    processingTime: '24 Hours', tenureOptions: '', overview: '',
    eligibility: '', purposes: '', documents: '', collateral: '',
    feeDetails: '', repaymentMethods: '', charges: '', imageUrl: '',
    benefits: '', features: '', emiFacility: '', prepaymentSlabs: '', balanceTransfer: '',
    loanMultiples: '', minTenure: '', tenureExtension: '', emiOptions: '',
    sanctionTimeline: '', documentCategories: '', guidelines: ''
  });
  const [loanFaqs, setLoanFaqs] = useState([{ q: '', a: '' }]);

  const navigate = useNavigate();

  // Authentication check
  useEffect(() => {
    const storedUser = localStorage.getItem('adminUser');
    if (!storedUser) { navigate('/admin/login'); return; }
    const parsedUser = JSON.parse(storedUser);
    if (!parsedUser.isLoggedIn) { navigate('/admin/login'); return; }
    setAdmin(parsedUser);
  }, [navigate]);

  // Fetch all Firestore data
  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Schemes
      const schemesSnap = await getDocs(collection(db, 'deposit_schemes'));
      const fbSchemes = [];
      schemesSnap.forEach((d) => fbSchemes.push({ id: d.id, ...d.data() }));

      const mergedSchemes = [...defaultDepositSchemes];
      if (fbSchemes.length > 0) {
        fbSchemes.forEach((fbS) => {
          const idx = mergedSchemes.findIndex(s => s.id === fbS.id);
          if (idx > -1) {
            mergedSchemes[idx] = fbS;
          } else {
            mergedSchemes.push(fbS);
          }
        });
      }
      setSchemes(mergedSchemes);

      // 2. Loans
      const loansSnap = await getDocs(collection(db, 'loan_products'));
      const loansList = [];
      loansSnap.forEach((d) => loansList.push({ id: d.id, ...d.data() }));

      const mergedLoans = [...defaultLoanProducts];
      if (loansList.length > 0) {
        loansList.forEach((fbL) => {
          const idx = mergedLoans.findIndex(l => l.id === fbL.id);
          if (idx > -1) {
            mergedLoans[idx] = fbL;
          } else {
            mergedLoans.push(fbL);
          }
        });
      }
      setLoanProducts(mergedLoans);

      // 3. Deposit Enquiries
      const depEnqSnap = await getDocs(collection(db, 'deposit_enquiries'));
      const depEnqList = [];
      depEnqSnap.forEach((d) => depEnqList.push({ id: d.id, ...d.data() }));
      depEnqList.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setDepositEnquiries(depEnqList);

      // 4. Loan Enquiries
      const loanEnqSnap = await getDocs(collection(db, 'loan_enquiries'));
      const loanEnqList = [];
      loanEnqSnap.forEach((d) => loanEnqList.push({ id: d.id, ...d.data() }));
      loanEnqList.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setLoanEnquiries(loanEnqList);

      // 5. Contact Enquiries
      const contactEnqSnap = await getDocs(collection(db, 'enquiries'));
      const contactEnqList = [];
      contactEnqSnap.forEach((d) => contactEnqList.push({ id: d.id, ...d.data() }));
      contactEnqList.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setContactEnquiries(contactEnqList);

      // 6. Complaints
      const contactCompSnap = await getDocs(collection(db, 'complaints'));
      const contactCompList = [];
      contactCompSnap.forEach((d) => contactCompList.push({ id: d.id, ...d.data() }));
      contactCompList.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setContactComplaints(contactCompList);

      // 7. Feedbacks
      const contactFeedSnap = await getDocs(collection(db, 'feedbacks'));
      const contactFeedList = [];
      contactFeedSnap.forEach((d) => contactFeedList.push({ id: d.id, ...d.data() }));
      contactFeedList.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setContactFeedbacks(contactFeedList);

      // 8. Account Enquiries
      const accountEnqSnap = await getDocs(collection(db, 'account_enquiries'));
      const accountEnqList = [];
      accountEnqSnap.forEach((d) => accountEnqList.push({ id: d.id, ...d.data() }));
      accountEnqList.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setAccountEnquiries(accountEnqList);

      // 9. Careers (Jobs)
      const careersSnap = await getDocs(collection(db, 'careers'));
      const fbCareers = [];
      careersSnap.forEach((d) => fbCareers.push({ id: d.id, ...d.data() }));
      const mergedCareers = [...mockCareers];
      if (fbCareers.length > 0) {
        fbCareers.forEach((fbC) => {
          const idx = mergedCareers.findIndex(c => c.id === fbC.id);
          if (idx > -1) {
            mergedCareers[idx] = fbC;
          } else {
            mergedCareers.push(fbC);
          }
        });
      }
      setCareersList(mergedCareers);

      // 10. Career Applications
      const appsSnap = await getDocs(collection(db, 'career_applications'));
      const fbApps = [];
      appsSnap.forEach((d) => fbApps.push({ id: d.id, ...d.data() }));
      const mergedApps = fbApps.length > 0 ? fbApps : [...mockCareerApplications];
      mergedApps.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setCareerApplications(mergedApps);

      // 11. News Articles
      const newsSnap = await getDocs(collection(db, 'news'));
      const fbNews = [];
      newsSnap.forEach((d) => fbNews.push({ id: d.id, ...d.data() }));
      const mergedNews = [...mockNews];
      if (fbNews.length > 0) {
        fbNews.forEach((fbN) => {
          const idx = mergedNews.findIndex(n => n.id === fbN.id);
          if (idx > -1) {
            mergedNews[idx] = fbN;
          } else {
            mergedNews.push(fbN);
          }
        });
      }
      setNewsList(mergedNews);

      // 12. PDF Notices
      const notSnap = await getDocs(collection(db, 'notices'));
      const fbNotices = [];
      notSnap.forEach((d) => fbNotices.push({ id: d.id, ...d.data() }));
      const mergedNotices = [...mockNotices];
      if (fbNotices.length > 0) {
        fbNotices.forEach((fbNot) => {
          const idx = mergedNotices.findIndex(n => n.id === fbNot.id);
          if (idx > -1) {
            mergedNotices[idx] = fbNot;
          } else {
            mergedNotices.push(fbNot);
          }
        });
      }
      setPdfNoticesList(mergedNotices);

      // 13. Leadership (Chairman & CEO)
      try {
        const chairSnap = await getDoc(doc(db, 'leadership', 'chairman'));
        if (chairSnap.exists()) {
          setChairmanData({ ...defaultChairmanData, ...chairSnap.data() });
        } else {
          setChairmanData(defaultChairmanData);
        }

        const ceoSnap = await getDoc(doc(db, 'leadership', 'ceo'));
        if (ceoSnap.exists()) {
          setCeoData({ ...defaultCeoData, ...ceoSnap.data() });
        } else {
          setCeoData(defaultCeoData);
        }
      } catch (e) {
        console.warn("Leadership fetch error:", e);
      }

      // 14. Board of Directors
      try {
        const dirSnap = await getDocs(collection(db, 'directors'));
        const fbDirectors = [];
        dirSnap.forEach((d) => fbDirectors.push({ id: d.id, ...d.data() }));
        if (fbDirectors.length > 0) {
          fbDirectors.sort((a, b) => (Number(a.order) || 99) - (Number(b.order) || 99));
          setDirectorsList(fbDirectors);
        } else {
          setDirectorsList(mockBoardOfDirectors);
        }
      } catch (e) {
        console.warn("Directors fetch error:", e);
      }

      const branchesSnap = await getDocs(collection(db, 'branches'));
      const adminsSnap = await getDocs(collection(db, 'admins'));

      setStats({
        schemesCount: mergedSchemes.length,
        loansCount: loansList.length,
        branchesCount: branchesSnap.size || 5,
        depositEnquiriesCount: depEnqList.length,
        loanEnquiriesCount: loanEnqList.length,
        adminsCount: adminsSnap.size || 1,
        careersCount: mergedCareers.length,
        applicationsCount: mergedApps.length,
        noticesCount: mergedNews.length + mergedNotices.length,
        directorsCount: directorsList.length || mockBoardOfDirectors.length
      });
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (admin) fetchData(); }, [admin]);

  const handleLogout = () => { localStorage.removeItem('adminUser'); navigate('/admin/login'); };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 4000);
  };

  // ========== DEPOSIT SCHEME HANDLERS ==========
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormMode('add'); setEditingId('');
    setFormData({
      id: '', name: '', tagline: '', rate: '', numericRate: 6.00, tenure: '',
      minDeposit: '₹1,000', numericMin: 1000, maxDeposit: 'No Limit',
      features: '', benefits: '', overview: '', eligibility: '', imageUrl: '',
      documents: '', interestPayouts: '', prematureWithdrawal: '', loanMargin: '', nominationFacility: '', terms: ''
    });
    setFaqs([{ q: '', a: '' }]);
    setBrochures([{ title: '', desc: '', pdfUrl: '', pdfName: '' }]);
  };

  const loadSchemeForEditing = (scheme) => {
    setFormMode('edit'); setEditingId(scheme.id);
    setFormData({
      id: scheme.id || '', name: scheme.name || '', tagline: scheme.tagline || '', rate: scheme.rate || '',
      numericRate: scheme.numericRate || 6.00, tenure: scheme.tenure || '', minDeposit: scheme.minDeposit || '₹1,000',
      numericMin: scheme.numericMin || 1000, maxDeposit: scheme.maxDeposit || 'No Limit',
      features: Array.isArray(scheme.features) ? scheme.features.join('\n') : (scheme.features || ''),
      benefits: Array.isArray(scheme.benefits) ? scheme.benefits.join('\n') : (scheme.benefits || ''),
      overview: scheme.overview || '', eligibility: scheme.eligibility || '', imageUrl: scheme.imageUrl || '',
      documents: scheme.documents || '', interestPayouts: scheme.interestPayouts || '',
      prematureWithdrawal: scheme.prematureWithdrawal || '', loanMargin: scheme.loanMargin || '',
      nominationFacility: scheme.nominationFacility || '', terms: scheme.terms || ''
    });
    setFaqs(scheme.faqs && scheme.faqs.length > 0 ? scheme.faqs : [{ q: '', a: '' }]);
    setBrochures(scheme.brochures && scheme.brochures.length > 0 ? scheme.brochures : [{ title: '', desc: '', pdfUrl: '', pdfName: '' }]);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { showNotification('error', 'Scheme name is required.'); return; }
    const schemeId = formMode === 'add'
      ? (formData.id.trim() ? formData.id.trim().toLowerCase().replace(/\s+/g, '-') : `scheme-${Date.now()}`)
      : editingId;

    const payload = {
      ...formData, id: schemeId, numericRate: parseFloat(formData.numericRate) || 6.0,
      numericMin: parseInt(formData.numericMin, 10) || 1000,
      features: formData.features ? formData.features.split('\n').filter(Boolean) : [],
      benefits: formData.benefits ? formData.benefits.split('\n').filter(Boolean) : [],
      faqs: faqs.filter(f => f.q.trim() && f.a.trim()),
      brochures: brochures.filter(b => b.title.trim()),
      updatedAt: new Date().toISOString()
    };

    setSubmitLoading(true);
    try {
      await setDoc(doc(db, 'deposit_schemes', schemeId), payload, { merge: true });
      showNotification('success', `Scheme "${formData.name}" saved successfully!`);
      resetForm(); fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    } finally { setSubmitLoading(false); }
  };

  const handleSchemeDelete = async (schemeId, schemeName) => {
    if (!window.confirm(`Are you sure you want to delete "${schemeName}"?`)) return;
    try {
      await deleteDoc(doc(db, 'deposit_schemes', schemeId));
      showNotification('success', `Deleted "${schemeName}".`);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    }
  };

  // ========== LOAN PRODUCT HANDLERS ==========
  const handleLoanInputChange = (e) => {
    const { name, value } = e.target;
    setLoanFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetLoanForm = () => {
    setLoanFormMode('add'); setLoanEditingId('');
    setLoanFormData({
      id: '', name: '', tagline: '', rate: '', numericRate: 10.50,
      maxAmount: '', numericMin: 50000, numericMax: 500000, defaultTenure: 36,
      processingTime: '24 Hours', tenureOptions: '', overview: '',
      eligibility: '', purposes: '', documents: '', collateral: '',
      feeDetails: '', repaymentMethods: '', charges: '', imageUrl: '',
      benefits: '', features: '', emiFacility: '', prepaymentSlabs: '', balanceTransfer: '',
      loanMultiples: '', minTenure: '', tenureExtension: '', emiOptions: '',
      sanctionTimeline: '', documentCategories: '', guidelines: ''
    });
    setLoanFaqs([{ q: '', a: '' }]);
  };

  const loadLoanForEditing = (loan) => {
    setLoanFormMode('edit'); setLoanEditingId(loan.id);
    setLoanFormData({
      id: loan.id || '', name: loan.name || '', tagline: loan.tagline || '', rate: loan.rate || '',
      numericRate: loan.numericRate || 10.50, maxAmount: loan.maxAmount || '',
      numericMin: loan.numericMin || 50000, numericMax: loan.numericMax || 500000,
      defaultTenure: loan.defaultTenure || 36, processingTime: loan.processingTime || '24 Hours',
      tenureOptions: loan.tenureOptions || '', overview: loan.overview || '', eligibility: loan.eligibility || '',
      purposes: Array.isArray(loan.purposes) ? loan.purposes.join('\n') : (loan.purposes || ''),
      documents: Array.isArray(loan.documents) ? loan.documents.join('\n') : (loan.documents || ''),
      collateral: loan.collateral || '', feeDetails: loan.feeDetails || '', repaymentMethods: loan.repaymentMethods || '',
      charges: loan.charges || '', imageUrl: loan.imageUrl || '',
      benefits: Array.isArray(loan.benefits) ? loan.benefits.join('\n') : (loan.benefits || ''),
      features: Array.isArray(loan.features) ? loan.features.join('\n') : (loan.features || ''),
      emiFacility: loan.emiFacility || '', prepaymentSlabs: loan.prepaymentSlabs || '',
      balanceTransfer: loan.balanceTransfer || '', loanMultiples: loan.loanMultiples || '',
      minTenure: loan.minTenure || '', tenureExtension: loan.tenureExtension || '',
      emiOptions: loan.emiOptions || '', sanctionTimeline: loan.sanctionTimeline || '',
      documentCategories: loan.documentCategories || '', guidelines: loan.guidelines || ''
    });
    setLoanFaqs(loan.faqs && loan.faqs.length > 0 ? loan.faqs : [{ q: '', a: '' }]);
  };

  const handleLoanFormSubmit = async (e) => {
    e.preventDefault();
    if (!loanFormData.name.trim()) { showNotification('error', 'Loan product name is required.'); return; }
    const loanId = loanFormMode === 'add'
      ? (loanFormData.id.trim() ? loanFormData.id.trim().toLowerCase().replace(/\s+/g, '-') : `loan-${Date.now()}`)
      : loanEditingId;

    const payload = {
      ...loanFormData, id: loanId, numericRate: parseFloat(loanFormData.numericRate) || 10.5,
      numericMin: parseInt(loanFormData.numericMin, 10) || 50000,
      numericMax: parseInt(loanFormData.numericMax, 10) || 500000,
      defaultTenure: parseInt(loanFormData.defaultTenure, 10) || 36,
      benefits: loanFormData.benefits ? loanFormData.benefits.split('\n').filter(Boolean) : [],
      features: loanFormData.features ? loanFormData.features.split('\n').filter(Boolean) : [],
      purposes: loanFormData.purposes ? loanFormData.purposes.split('\n').filter(Boolean) : [],
      documents: loanFormData.documents ? loanFormData.documents.split('\n').filter(Boolean) : [],
      faqs: loanFaqs.filter(f => f.q.trim() && f.a.trim()),
      updatedAt: new Date().toISOString()
    };

    setSubmitLoading(true);
    try {
      await setDoc(doc(db, 'loan_products', loanId), payload, { merge: true });
      showNotification('success', `Loan "${loanFormData.name}" saved successfully!`);
      resetLoanForm(); fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    } finally { setSubmitLoading(false); }
  };

  const handleLoanDelete = async (loanId, loanName) => {
    if (!window.confirm(`Are you sure you want to delete "${loanName}"?`)) return;
    try {
      await deleteDoc(doc(db, 'loan_products', loanId));
      showNotification('success', `Deleted "${loanName}".`);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    }
  };

  // ========== CAREERS (JOB) HANDLERS ==========
  const handleJobInputChange = (e) => {
    const { name, value } = e.target;
    setJobFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetJobForm = () => {
    setJobFormMode('add');
    setJobEditingId('');
    setJobFormData({
      id: '',
      title: '',
      department: 'Branch Operations',
      skills: '',
      qualification: '',
      vacancies: '',
      location: '',
      experience: '',
      type: 'Full-Time',
      salary: '',
      description: ''
    });
  };

  const openCreateJobModal = () => {
    resetJobForm();
    setJobModalOpen(true);
  };

  const loadJobForEditing = (job) => {
    setJobFormMode('edit');
    setJobEditingId(job.id);
    setJobFormData({
      id: job.id || '',
      title: job.title || '',
      department: job.department || 'Branch Operations',
      skills: job.skills || '',
      qualification: job.qualification || '',
      vacancies: job.vacancies || '',
      location: job.location || '',
      experience: job.experience || '',
      type: job.type || 'Full-Time',
      salary: job.salary || '',
      description: job.description || ''
    });
    setJobModalOpen(true);
  };

  const handleJobFormSubmit = async (e) => {
    e.preventDefault();
    if (!jobFormData.title.trim()) {
      showNotification('error', 'Job Title is required.');
      return;
    }

    const jobId = jobFormMode === 'add'
      ? (jobFormData.id.trim() ? jobFormData.id.trim() : `job_${Date.now()}`)
      : jobEditingId;

    const payload = {
      ...jobFormData,
      id: jobId,
      updatedAt: new Date().toISOString()
    };

    setSubmitLoading(true);
    try {
      await setDoc(doc(db, 'careers', jobId), payload, { merge: true });
      showNotification('success', `Job vacancy "${jobFormData.title}" ${jobFormMode === 'add' ? 'created' : 'updated'} successfully!`);
      setJobModalOpen(false);
      resetJobForm();
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleJobDelete = async (jobId, jobTitle) => {
    if (!window.confirm(`Are you sure you want to delete the job posting "${jobTitle}"?`)) return;
    try {
      await deleteDoc(doc(db, 'careers', jobId));
      showNotification('success', `Job posting "${jobTitle}" deleted.`);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    }
  };

  // ========== CAREER APPLICATIONS HANDLERS ==========
  const handleApplicationStatusChange = async (appId, newStatus) => {
    try {
      await updateDoc(doc(db, 'career_applications', appId), { status: newStatus });
      setCareerApplications(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
      showNotification('success', `Application status updated to "${newStatus}".`);
    } catch (err) {
      showNotification('error', `Failed to update status: ${err.message}`);
    }
  };

  const handleApplicationDelete = async (appId, applicantName) => {
    if (!window.confirm(`Delete application submitted by ${applicantName || 'this candidate'}?`)) return;
    try {
      await deleteDoc(doc(db, 'career_applications', appId));
      setCareerApplications(prev => prev.filter(a => a.id !== appId));
      showNotification('success', `Application deleted.`);
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    }
  };

  // ========== MANAGE NOTICES HANDLERS ==========
  const handleCategoryInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePdfNoticeInputChange = (e) => {
    const { name, value } = e.target;
    setPdfNoticeFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNoticePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1.5 * 1024 * 1024) {
        showNotification('error', 'Notice PDF size must be under 1.5MB.');
        e.target.value = null;
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPdfNoticeFormData(prev => ({
          ...prev,
          pdfUrl: reader.result,
          pdfName: file.name,
          fileSize: (file.size / 1024).toFixed(0) + ' KB'
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const openCreateNoticeModal = () => {
    setCategoryFormMode('add');
    setCategoryEditingId('');
    setCategoryFormData({
      id: '',
      title: '',
      category: 'Announcements',
      date: new Date().toISOString().split('T')[0],
      summary: '',
      content: '',
      isCircular: false
    });
    setPdfNoticeFormMode('add');
    setPdfNoticeEditingId('');
    setPdfNoticeFormData({
      id: '',
      title: '',
      noticeNo: '',
      date: new Date().toISOString().split('T')[0],
      department: 'Administration',
      description: '',
      pdfUrl: '',
      pdfName: '',
      fileSize: ''
    });
    setNoticeModalOpen(true);
  };

  const loadCategoryNewsForEditing = (item) => {
    setCategoryFormMode('edit');
    setCategoryEditingId(item.id);
    setCategoryFormData({
      id: item.id || '',
      title: item.title || '',
      category: item.category || 'Announcements',
      date: item.date || new Date().toISOString().split('T')[0],
      summary: item.summary || '',
      content: item.content || '',
      isCircular: !!item.isCircular
    });
    setNoticeFormToggle('category');
    setNoticeModalOpen(true);
  };

  const loadPdfNoticeForEditing = (item) => {
    setPdfNoticeFormMode('edit');
    setPdfNoticeEditingId(item.id);
    setPdfNoticeFormData({
      id: item.id || '',
      title: item.title || '',
      noticeNo: item.noticeNo || '',
      date: item.date || new Date().toISOString().split('T')[0],
      department: item.department || 'Administration',
      description: item.description || '',
      pdfUrl: item.pdfUrl || '',
      pdfName: item.pdfName || '',
      fileSize: item.fileSize || ''
    });
    setNoticeFormToggle('pdf');
    setNoticeModalOpen(true);
  };

  const handleCategoryNewsSubmit = async (e) => {
    e.preventDefault();
    if (!categoryFormData.title.trim()) {
      showNotification('error', 'Notice Title is required.');
      return;
    }
    const newsId = categoryFormMode === 'add'
      ? (categoryFormData.id.trim() ? categoryFormData.id.trim() : `n_${Date.now()}`)
      : categoryEditingId;

    const payload = {
      ...categoryFormData,
      id: newsId,
      updatedAt: new Date().toISOString()
    };

    setSubmitLoading(true);
    try {
      await setDoc(doc(db, 'news', newsId), payload, { merge: true });
      showNotification('success', `News article "${categoryFormData.title}" saved successfully!`);
      setNoticeModalOpen(false);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePdfNoticeSubmit = async (e) => {
    e.preventDefault();
    if (!pdfNoticeFormData.title.trim()) {
      showNotification('error', 'Notice Title is required.');
      return;
    }
    const noticeId = pdfNoticeFormMode === 'add'
      ? (pdfNoticeFormData.id.trim() ? pdfNoticeFormData.id.trim() : `not_${Date.now()}`)
      : pdfNoticeEditingId;

    const payload = {
      ...pdfNoticeFormData,
      id: noticeId,
      updatedAt: new Date().toISOString()
    };

    setSubmitLoading(true);
    try {
      await setDoc(doc(db, 'notices', noticeId), payload, { merge: true });
      showNotification('success', `Official Notice "${pdfNoticeFormData.title}" saved successfully!`);
      setNoticeModalOpen(false);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCategoryNewsDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await deleteDoc(doc(db, 'news', id));
      showNotification('success', `Deleted news article "${title}".`);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    }
  };

  const handlePdfNoticeDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await deleteDoc(doc(db, 'notices', id));
      showNotification('success', `Deleted official notice "${title}".`);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    }
  };

  // ========== MANAGE MEMBERS / LEADERSHIP HANDLERS ==========
  const handleChairmanInputChange = (e) => {
    const { name, value } = e.target;
    setChairmanData(prev => ({ ...prev, [name]: value }));
  };

  const handleChairmanImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1.2 * 1024 * 1024) {
        showNotification('error', 'Image size must be under 1.2MB.');
        e.target.value = null;
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setChairmanData(prev => ({ ...prev, imageUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChairmanSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      await setDoc(doc(db, 'leadership', 'chairman'), {
        ...chairmanData,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      showNotification('success', 'Chairman Message & Profile updated successfully!');
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCeoInputChange = (e) => {
    const { name, value } = e.target;
    setCeoData(prev => ({ ...prev, [name]: value }));
  };

  const handleCeoImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1.2 * 1024 * 1024) {
        showNotification('error', 'Image size must be under 1.2MB.');
        e.target.value = null;
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCeoData(prev => ({ ...prev, imageUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCeoSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      await setDoc(doc(db, 'leadership', 'ceo'), {
        ...ceoData,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      showNotification('success', 'CEO / GM Message & Profile updated successfully!');
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  // ========== DIRECTORS CRUD HANDLERS ==========
  const handleDirectorInputChange = (e) => {
    const { name, value } = e.target;
    setDirectorFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDirectorImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1.2 * 1024 * 1024) {
        showNotification('error', 'Photo size must be under 1.2MB.');
        e.target.value = null;
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setDirectorFormData(prev => ({ ...prev, imageUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const openCreateDirectorModal = () => {
    setDirectorFormMode('add');
    setDirectorEditingId('');
    setDirectorFormData({
      id: '',
      name: '',
      role: 'Director',
      description: '',
      imageUrl: '',
      order: directorsList.length + 1
    });
    setDirectorModalOpen(true);
  };

  const loadDirectorForEditing = (dir) => {
    setDirectorFormMode('edit');
    setDirectorEditingId(dir.id);
    setDirectorFormData({
      id: dir.id || '',
      name: dir.name || '',
      role: dir.role || 'Director',
      description: dir.description || '',
      imageUrl: dir.imageUrl || '',
      order: dir.order || 1
    });
    setDirectorModalOpen(true);
  };

  const handleDirectorFormSubmit = async (e) => {
    e.preventDefault();
    if (!directorFormData.name.trim()) {
      showNotification('error', 'Director Name is required.');
      return;
    }

    const dirId = directorFormMode === 'add'
      ? (directorFormData.id.trim() ? directorFormData.id.trim() : `dir_${Date.now()}`)
      : directorEditingId;

    const payload = {
      ...directorFormData,
      id: dirId,
      order: Number(directorFormData.order) || 99,
      updatedAt: new Date().toISOString()
    };

    setSubmitLoading(true);
    try {
      await setDoc(doc(db, 'directors', dirId), payload, { merge: true });
      showNotification('success', `Director "${directorFormData.name}" ${directorFormMode === 'add' ? 'added' : 'updated'} successfully!`);
      setDirectorModalOpen(false);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDirectorDelete = async (dirId, dirName) => {
    if (!window.confirm(`Are you sure you want to remove director "${dirName}" from the Board?`)) return;
    try {
      await deleteDoc(doc(db, 'directors', dirId));
      showNotification('success', `Director "${dirName}" removed.`);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    }
  };

  // ========== ENQUIRY ACTIONS ==========
  const updateEnquiryStatus = async (collectionName, id, status) => {
    try {
      await updateDoc(doc(db, collectionName, id), { status });
      showNotification('success', `Status updated to "${status}".`);
      fetchData();
    } catch (err) {
      showNotification('error', `Failed to update status: ${err.message}`);
    }
  };

  const deleteEnquiry = async (collectionName, id) => {
    if (!window.confirm('Delete this enquiry record?')) return;
    try {
      await deleteDoc(doc(db, collectionName, id));
      showNotification('success', 'Enquiry deleted.');
      fetchData();
    } catch (err) {
      showNotification('error', `Failed: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Done': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Followed Up': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Shortlisted': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Applied': return 'bg-blue-50 text-blue-700 border-blue-200';
      default: return 'bg-blue-50 text-blue-700 border-blue-200';
    }
  };

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center space-y-4">
          <Loader className="h-8 w-8 animate-spin text-[#B31825] mx-auto" />
          <p className="text-sm text-gray-500">Checking credentials...</p>
        </div>
      </div>
    );
  }

  const sidebarLinks = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'manage-deposits', label: 'Manage Deposits', icon: Landmark },
    { id: 'manage-loans', label: 'Manage Loans', icon: CreditCard },
    { id: 'manage-members', label: 'Manage Members', icon: UserCheck },
    { id: 'careers', label: 'Careers', icon: Briefcase },
    { id: 'manage-notices', label: 'Manage Notices', icon: Bell },
    { id: 'deposit-enquiries', label: 'Deposit Enquiries', icon: ClipboardList },
    { id: 'loan-enquiries', label: 'Loan Enquiries', icon: FileText },
    { id: 'account-enquiries', label: 'Account Enquiries', icon: Users },
    { id: 'contact', label: 'Contact', icon: Phone },
  ];

  // ========== HELPER FORM INPUTS ==========
  const FormInput = ({ label, name, value, onChange, placeholder, type = "text", hint, disabled }) => (
    <div className="space-y-1">
      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B31825]/20 focus:border-[#B31825] text-xs transition-colors disabled:bg-gray-100"
      />
      {hint && <p className="text-[10px] text-gray-400 font-light">{hint}</p>}
    </div>
  );

  const FormTextarea = ({ label, name, value, onChange, placeholder, rows = 3, hint }) => (
    <div className="space-y-1">
      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600">{label}</label>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B31825]/20 focus:border-[#B31825] text-xs transition-colors"
      />
      {hint && <p className="text-[10px] text-gray-400 font-light">{hint}</p>}
    </div>
  );

  // ========== SCHEME LIST COMPONENT ==========
  const SchemeList = ({ items, onEdit, onDelete, onReset, label }) => (
    <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm space-y-4">
      <div className="flex justify-between items-center border-b border-gray-100 pb-3">
        <h3 className="font-montserrat font-bold text-sm text-[#052046]">Active {label} ({items.length})</h3>
        <button onClick={onReset} className="text-xs text-[#B31825] font-bold uppercase flex items-center hover:underline">
          <PlusCircle className="h-4 w-4 mr-1" /><span>New</span>
        </button>
      </div>
      {loading ? (
        <div className="text-center py-10"><Loader className="h-6 w-6 animate-spin text-[#B31825] mx-auto" /><p className="text-xs text-gray-400 mt-2">Querying...</p></div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-2xl"><p className="text-xs text-gray-400">No custom {label.toLowerCase()} in database.</p></div>
      ) : (
        <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto pr-1">
          {items.map((item) => (
            <div key={item.id} className="py-3.5 flex justify-between items-center group">
              <div>
                <h4 className="font-bold text-sm text-navy">{item.name}</h4>
                <span className="text-[10px] font-mono text-[#B31825] font-semibold">{item.id}</span>
                <span className="text-gray-400 text-[10px] block font-light mt-0.5">{item.rate}</span>
              </div>
              <div className="flex items-center space-x-2 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity">
                <button onClick={() => onEdit(item)} className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors" title="Edit"><Edit className="h-3.5 w-3.5" /></button>
                <button onClick={() => onDelete(item.id, item.name)} className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ========== ENQUIRY CARDS ==========
  const EnquiryCard = ({ enq, collectionName, fields }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow space-y-4 relative group">
      <div className="flex justify-between items-start">
        <div className="space-y-1">
          <h4 className="font-montserrat font-bold text-sm text-[#052046]">{enq.name || 'N/A'}</h4>
          <p className="text-[10px] text-gray-400 font-mono">{enq.createdAt || 'No date'}</p>
        </div>
        <span className={`text-[9px] font-bold uppercase px-2.5 py-1 rounded-full border ${getStatusBadge(enq.status)}`}>
          {enq.status || 'New'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {fields.map((f, i) => (
          <div key={i} className="flex items-center space-x-1.5 text-gray-500">
            <f.icon className="h-3 w-3 text-[#B31825] shrink-0" />
            <span className="truncate">{enq[f.key] || '—'}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
        <button onClick={() => setViewingEnquiry(enq)} className="flex items-center space-x-1 px-3 py-1.5 bg-[#052046] text-white text-[9px] font-bold uppercase rounded-lg hover:bg-[#0c346a] transition-colors">
          <Eye className="h-3 w-3" /><span>View</span>
        </button>
        <button onClick={() => updateEnquiryStatus(collectionName, enq.id, 'Done')} className="flex items-center space-x-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase rounded-lg hover:bg-emerald-100 transition-colors border border-emerald-200">
          <CheckCircle className="h-3 w-3" /><span>Done</span>
        </button>
        <button onClick={() => updateEnquiryStatus(collectionName, enq.id, 'Followed Up')} className="flex items-center space-x-1 px-3 py-1.5 bg-amber-50 text-amber-700 text-[9px] font-bold uppercase rounded-lg hover:bg-amber-100 transition-colors border border-amber-200">
          <Phone className="h-3 w-3" /><span>Follow Up</span>
        </button>
        <button onClick={() => deleteEnquiry(collectionName, enq.id)} className="flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-red-600 text-[9px] font-bold uppercase rounded-lg hover:bg-red-100 transition-colors border border-red-200">
          <Trash2 className="h-3 w-3" /><span>Delete</span>
        </button>
      </div>
    </div>
  );

  const ContactEnquiryCard = ({ enq, collectionName }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow space-y-3.5">
      <div className="flex justify-between items-start">
        <div>
          <h4 className="font-montserrat font-bold text-sm text-[#052046]">{enq.fullName || enq.name || 'N/A'}</h4>
          <p className="text-[10px] text-gray-400 font-mono mt-0.5">{enq.createdAt || 'No date'}</p>
        </div>
        <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full border ${getStatusBadge(enq.status)}`}>
          {enq.status || 'New'}
        </span>
      </div>
      <div className="space-y-1 text-xs text-gray-600">
        <div className="flex items-center space-x-1.5"><Phone className="h-3 w-3 text-[#B31825]" /><span>{enq.phone || '—'}</span></div>
        <div className="flex items-center space-x-1.5"><Mail className="h-3 w-3 text-[#B31825]" /><span>{enq.email || '—'}</span></div>
        {enq.subject && <div className="text-[11px] font-semibold text-navy pt-1">Subject: {enq.subject}</div>}
      </div>
      {enq.message && <p className="text-xs text-gray-500 bg-gray-50 p-2.5 rounded-xl line-clamp-2 italic font-light">"{enq.message}"</p>}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
        <button onClick={() => setViewingEnquiry(enq)} className="px-2.5 py-1 bg-[#052046] text-white text-[9px] font-bold uppercase rounded-lg hover:bg-[#0c346a]">View</button>
        <button onClick={() => updateEnquiryStatus(collectionName, enq.id, 'Done')} className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase rounded-lg hover:bg-emerald-100 border border-emerald-200">Done</button>
        <button onClick={() => deleteEnquiry(collectionName, enq.id)} className="px-2.5 py-1 bg-red-50 text-red-600 text-[9px] font-bold uppercase rounded-lg hover:bg-red-100 border border-red-200">Delete</button>
      </div>
    </div>
  );

  const AccountEnquiryCard = ({ enq, collectionName }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow space-y-3.5">
      <div className="flex justify-between items-start">
        <div>
          <h4 className="font-montserrat font-bold text-sm text-[#052046]">{enq.fullName || 'N/A'}</h4>
          <p className="text-[10px] text-gray-400 font-mono mt-0.5">{enq.createdAt || 'No date'}</p>
        </div>
        <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full border ${getStatusBadge(enq.status)}`}>
          {enq.status || 'New'}
        </span>
      </div>
      <div className="space-y-1 text-xs text-gray-600">
        <div className="flex items-center space-x-1.5"><Phone className="h-3 w-3 text-[#B31825]" /><span>{enq.phone || '—'}</span></div>
        <div className="flex items-center space-x-1.5"><Mail className="h-3 w-3 text-[#B31825]" /><span>{enq.email || '—'}</span></div>
        <div className="flex items-center space-x-1.5"><MapPin className="h-3 w-3 text-[#B31825]" /><span>{enq.city || '—'}</span></div>
        <div className="text-[11px] font-bold text-[#052046] pt-1">Account: {enq.accountType || '—'}</div>
      </div>
      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
        <button onClick={() => setViewingEnquiry(enq)} className="px-2.5 py-1 bg-[#052046] text-white text-[9px] font-bold uppercase rounded-lg hover:bg-[#0c346a]">View</button>
        <button onClick={() => updateEnquiryStatus(collectionName, enq.id, 'Followed Up')} className="px-2.5 py-1 bg-amber-50 text-amber-700 text-[9px] font-bold uppercase rounded-lg hover:bg-amber-100 border border-amber-200">Follow Up</button>
        <button onClick={() => deleteEnquiry(collectionName, enq.id)} className="px-2.5 py-1 bg-red-50 text-red-600 text-[9px] font-bold uppercase rounded-lg hover:bg-red-100 border border-red-200">Delete</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row font-sans text-gray-800">
      
      {/* Sidebar */}
      <aside className="w-full md:w-64 bg-[#052046] text-white flex flex-col shrink-0 relative z-20 shadow-xl">
        <div className="p-6 border-b border-white/10 flex items-center space-x-3.5">
          <div className="h-10 w-10 rounded-xl bg-[#B31825] flex items-center justify-center text-white"><User className="h-5 w-5" /></div>
          <div>
            <h4 className="font-montserrat font-bold text-sm leading-snug">{admin.name}</h4>
            <span className="text-[10px] uppercase font-bold text-golden tracking-wider">{admin.role}</span>
          </div>
        </div>
        <nav className="flex-grow p-4 space-y-1.5 mt-2 overflow-y-auto">
          {sidebarLinks.map(link => {
            const Icon = link.icon;
            return (
              <button key={link.id} onClick={() => setActiveTab(link.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all ${activeTab === link.id ? 'bg-[#B31825] text-white shadow-md' : 'text-gray-300 hover:bg-white/5 hover:text-white'}`}>
                <Icon className="h-4 w-4 shrink-0" /><span>{link.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center space-x-3.5 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wider text-red-300 hover:bg-white/5 hover:text-white transition-all">
            <LogOut className="h-4 w-4" /><span>Logout Portal</span>
          </button>
        </div>
      </aside>

      {/* Main Panel */}
      <main className="flex-grow p-6 sm:p-8 overflow-y-auto max-h-screen">
        
        {/* Notification Toast */}
        {notification.message && (
          <div className={`fixed top-6 right-6 z-50 px-5 py-3.5 rounded-2xl shadow-xl flex items-center space-x-3 text-sm font-semibold border ${notification.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            {notification.type === 'success' ? <CheckCircle className="h-5 w-5 text-emerald-600" /> : <AlertCircle className="h-5 w-5 text-red-600" />}
            <span>{notification.message}</span>
          </div>
        )}

        {/* Enquiry View Modal */}
        {viewingEnquiry && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden border border-gray-100">
              <div className="bg-[#052046] text-white p-6 relative">
                <h3 className="font-montserrat font-extrabold text-lg">Enquiry Details</h3>
                <p className="text-gray-300 text-xs mt-1 font-light">Submitted: {viewingEnquiry.createdAt || 'N/A'}</p>
                <button onClick={() => setViewingEnquiry(null)} className="absolute top-6 right-6 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
                {Object.entries(viewingEnquiry).filter(([k]) => k !== 'id').map(([key, val]) => (
                  <div key={key} className="flex items-start border-b border-gray-100 pb-2.5">
                    <span className="text-[10px] font-bold text-[#B31825] uppercase w-32 shrink-0 pt-0.5">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-xs text-gray-700 flex-grow">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-gray-100 flex justify-end">
                <button onClick={() => setViewingEnquiry(null)} className="px-5 py-2 bg-gray-100 text-gray-600 rounded-xl text-xs font-bold uppercase hover:bg-gray-200 transition-colors">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* ==================== TAB: DASHBOARD ==================== */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Metrics Console</span>
                <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Co-operative General Overview</h1>
                <p className="text-gray-500 text-xs font-light">Audit metrics updated in real-time from active collections.</p>
              </div>
              <button onClick={fetchData} className="px-5 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all">Sync Data</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { label: 'Deposit Schemes', val: stats.schemesCount, icon: Landmark, color: 'bg-emerald-50 text-emerald-600' },
                { label: 'Loan Products', val: stats.loansCount, icon: CreditCard, color: 'bg-violet-50 text-violet-600' },
                { label: 'Board of Directors', val: directorsList.length || 5, icon: UserCheck, color: 'bg-indigo-50 text-indigo-600' },
                { label: 'Open Job Vacancies', val: stats.careersCount, icon: Briefcase, color: 'bg-blue-50 text-blue-600' },
                { label: 'Job Applications', val: stats.applicationsCount, icon: GraduationCap, color: 'bg-rose-50 text-rose-600' },
                { label: 'Notices & Circulars', val: stats.noticesCount, icon: Bell, color: 'bg-amber-50 text-amber-600' },
              ].map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div key={idx} className="bg-white border border-gray-200 rounded-3xl p-6 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{card.label}</span>
                      <span className="font-montserrat font-extrabold text-3xl text-[#052046] block font-mono">{card.val}</span>
                    </div>
                    <div className={`p-4 rounded-2xl ${card.color}`}><Icon className="h-6 w-6" /></div>
                  </div>
                );
              })}
            </div>

            <div className="bg-[#052046] text-white rounded-3xl p-8 relative overflow-hidden shadow-lg">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-bl-full" />
              <div className="max-w-2xl space-y-3 relative z-10">
                <span className="text-golden font-bold uppercase tracking-wider text-[10px] flex items-center"><Sparkles className="h-4 w-4 mr-2" /> System Operations Guide</span>
                <h3 className="font-montserrat font-extrabold text-lg text-white">Dynamic Interface Management</h3>
                <p className="text-gray-300 text-xs font-light leading-relaxed">
                  Manage deposit schemes, loan portfolios, Chairman & CEO statements, dynamic Board of Directors, job vacancies, received applications, and official PDF notices. All modifications synchronize directly to the public website in real time.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ==================== TAB: MANAGE DEPOSITS ==================== */}
        {activeTab === 'manage-deposits' && (
          <div className="space-y-8">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Deposit Portfolios</span>
              <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Manage Investment Schemes</h1>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <SchemeList items={schemes} onEdit={loadSchemeForEditing} onDelete={handleSchemeDelete} onReset={resetForm} label="Schemes" />
              </div>
              <div className="lg:col-span-7">
                <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
                  <div className="border-b border-gray-100 pb-4">
                    <h3 className="font-montserrat font-bold text-[#052046] flex items-center"><Sparkles className="h-5 w-5 text-[#B31825] mr-2" />{formMode === 'add' ? 'Create Deposit Scheme' : 'Update Deposit Scheme'}</h3>
                  </div>
                  <form onSubmit={handleFormSubmit} className="space-y-5 text-navy text-xs">
                    <FormInput label="Unique ID (Slug)" name="id" value={formData.id} onChange={handleInputChange} disabled={formMode === 'edit'} placeholder="e.g. fixed-dep" hint={formMode === 'add' ? 'Lowercase, use hyphens. Cannot change later.' : ''} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormInput label="Scheme Name" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. Fixed Deposit (FD)" />
                      <FormInput label="Interest Rate Display" name="rate" value={formData.rate} onChange={handleInputChange} placeholder="e.g. 6.75% - 8.25% p.a." />
                    </div>
                    <FormInput label="Short Tagline" name="tagline" value={formData.tagline} onChange={handleInputChange} placeholder="Summary of scheme advantages" />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormInput label="Numeric Rate (%)" name="numericRate" value={formData.numericRate} onChange={handleInputChange} type="number" placeholder="8.25" />
                      <FormInput label="Min Deposit Display" name="minDeposit" value={formData.minDeposit} onChange={handleInputChange} placeholder="₹1,000" />
                      <FormInput label="Tenure Period" name="tenure" value={formData.tenure} onChange={handleInputChange} placeholder="7 Days to 10 Yrs" />
                    </div>
                    <FormTextarea label="Features (1 per line)" name="features" value={formData.features} onChange={handleInputChange} placeholder="High returns&#10;Quarterly compounding&#10;Nomination facility" />
                    <FormTextarea label="Overview Details" name="overview" value={formData.overview} onChange={handleInputChange} placeholder="Detailed explanation of this deposit product..." />
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                      <button type="button" onClick={resetForm} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold uppercase text-xs hover:bg-gray-50">Reset</button>
                      <button type="submit" disabled={submitLoading} className="px-6 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase shadow-md">{submitLoading ? 'Saving...' : (formMode === 'add' ? 'Publish Scheme' : 'Update Scheme')}</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== TAB: MANAGE LOANS ==================== */}
        {activeTab === 'manage-loans' && (
          <div className="space-y-8">
            <div className="space-y-1">
              <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Loan Portfolio</span>
              <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Manage Loan Products</h1>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <SchemeList items={loanProducts} onEdit={loadLoanForEditing} onDelete={handleLoanDelete} onReset={resetLoanForm} label="Loans" />
              </div>
              <div className="lg:col-span-7">
                <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
                  <div className="border-b border-gray-100 pb-4">
                    <h3 className="font-montserrat font-bold text-[#052046] flex items-center"><CreditCard className="h-5 w-5 text-[#B31825] mr-2" />{loanFormMode === 'add' ? 'Create Loan Product' : 'Update Loan Product'}</h3>
                  </div>
                  <form onSubmit={handleLoanFormSubmit} className="space-y-5 text-navy text-xs">
                    <FormInput label="Unique ID (Slug)" name="id" value={loanFormData.id} onChange={handleLoanInputChange} disabled={loanFormMode === 'edit'} placeholder="e.g. gold-loan" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormInput label="Product Name" name="name" value={loanFormData.name} onChange={handleLoanInputChange} placeholder="e.g. Gold Loan" />
                      <FormInput label="Interest Rate" name="rate" value={loanFormData.rate} onChange={handleLoanInputChange} placeholder="e.g. 9.25% p.a." />
                    </div>
                    <FormInput label="Short Tagline" name="tagline" value={loanFormData.tagline} onChange={handleLoanInputChange} placeholder="Instant funds against gold ornaments" />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormInput label="Numeric Rate (%)" name="numericRate" value={loanFormData.numericRate} onChange={handleLoanInputChange} type="number" placeholder="9.25" />
                      <FormInput label="Max Amount Display" name="maxAmount" value={loanFormData.maxAmount} onChange={handleLoanInputChange} placeholder="₹25,00,000" />
                      <FormInput label="Processing Time" name="processingTime" value={loanFormData.processingTime} onChange={handleLoanInputChange} placeholder="30 Minutes" />
                    </div>
                    <FormTextarea label="Eligibility Criteria" name="eligibility" value={loanFormData.eligibility} onChange={handleLoanInputChange} placeholder="Individuals aged 18-70 with genuine gold ornaments..." />
                    <FormTextarea label="Required Documents (1 per line)" name="documents" value={loanFormData.documents} onChange={handleLoanInputChange} placeholder="PAN Card&#10;Aadhar Card&#10;Passport Photos" />
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                      <button type="button" onClick={resetLoanForm} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold uppercase text-xs hover:bg-gray-50">Reset</button>
                      <button type="submit" disabled={submitLoading} className="px-6 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase shadow-md">{submitLoading ? 'Saving...' : (loanFormMode === 'add' ? 'Publish Loan' : 'Update Loan')}</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== TAB: MANAGE MEMBERS / LEADERSHIP ==================== */}
        {activeTab === 'manage-members' && (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Governance & Administration</span>
                <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Manage Leadership & Board of Directors</h1>
                <p className="text-gray-500 text-xs font-light">Update Chairman & CEO messages and perform full CRUD operations on Board of Directors.</p>
              </div>
              <div className="flex items-center space-x-3">
                {membersActiveTab === 'directors' && (
                  <button
                    onClick={openCreateDirectorModal}
                    className="px-5 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all flex items-center space-x-2 shadow-md"
                  >
                    <PlusCircle className="h-4 w-4" />
                    <span>Add New Director</span>
                  </button>
                )}
                <button onClick={fetchData} className="px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-bold rounded-xl text-xs uppercase tracking-wider transition-all">
                  Refresh
                </button>
              </div>
            </div>

            {/* 2-Toggle Switcher */}
            <div className="flex bg-white p-1 rounded-2xl border border-gray-200 max-w-lg shadow-sm">
              <button
                type="button"
                onClick={() => setMembersActiveTab('leadership')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 flex items-center justify-center space-x-2 ${
                  membersActiveTab === 'leadership' ? 'bg-[#052046] text-white shadow-sm' : 'text-gray-600 hover:text-[#052046]'
                }`}
              >
                <User className="h-4 w-4" />
                <span>Chairman & CEO Messages</span>
              </button>

              <button
                type="button"
                onClick={() => setMembersActiveTab('directors')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 flex items-center justify-center space-x-2 ${
                  membersActiveTab === 'directors' ? 'bg-[#B31825] text-white shadow-sm' : 'text-gray-600 hover:text-[#B31825]'
                }`}
              >
                <Users className="h-4 w-4" />
                <span>Board of Directors ({directorsList.length})</span>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-gray-200">
                <Loader className="h-8 w-8 animate-spin text-[#B31825] mx-auto" />
                <p className="text-xs text-gray-400 mt-2">Loading leadership records...</p>
              </div>
            ) : (
              <>
                {/* ========== TOGGLE 1: CHAIRMAN & CEO MESSAGES ========== */}
                {membersActiveTab === 'leadership' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* 1. Chairman Form */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
                      <div className="border-b border-gray-100 pb-4 flex items-center justify-between">
                        <div>
                          <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider block">Board Governance</span>
                          <h3 className="font-montserrat font-bold text-base text-[#052046]">Chairman Statement & Profile</h3>
                        </div>
                        <span className="text-[10px] font-mono bg-[#B31825]/10 text-[#B31825] px-2.5 py-1 rounded-md font-bold uppercase">Chairman</span>
                      </div>

                      <form onSubmit={handleChairmanSubmit} className="space-y-4 text-xs text-gray-700">
                        {/* Avatar / Photo preview */}
                        <div className="flex items-center space-x-4 p-3.5 bg-[#F8FAFC] rounded-2xl border border-gray-150">
                          <div className="h-16 w-16 rounded-full bg-navy flex items-center justify-center text-golden overflow-hidden border-2 border-[#B31825] shrink-0">
                            {chairmanData.imageUrl ? (
                              <img src={chairmanData.imageUrl} alt="Chairman" className="w-full h-full object-cover" />
                            ) : (
                              <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                                <rect width="100" height="100" fill="#020C1A" />
                                <circle cx="50" cy="40" r="18" fill="#B31825" />
                                <path d="M25 80 C25 60, 75 60, 75 80 Z" fill="#E5E7EB" />
                              </svg>
                            )}
                          </div>
                          <div className="space-y-1 flex-1">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase">Chairman Photo / Portrait</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleChairmanImageUpload}
                              className="text-[11px] text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-[10px] file:font-bold file:bg-[#052046] file:text-white hover:file:bg-[#0c346a] cursor-pointer"
                            />
                            {chairmanData.imageUrl && (
                              <button
                                type="button"
                                onClick={() => setChairmanData(prev => ({ ...prev, imageUrl: '' }))}
                                className="text-[10px] text-red-500 hover:underline block pt-0.5"
                              >
                                Remove custom photo
                              </button>
                            )}
                          </div>
                        </div>

                        <FormInput
                          label="Chairman Full Name *"
                          name="name"
                          value={chairmanData.name}
                          onChange={handleChairmanInputChange}
                          placeholder="e.g. Shri. Balasaheb A. Bhosale"
                        />

                        <FormInput
                          label="Designation / Role Title *"
                          name="role"
                          value={chairmanData.role}
                          onChange={handleChairmanInputChange}
                          placeholder="e.g. Chairman, Sahyadri Board"
                        />

                        <FormInput
                          label="Message Heading / Title *"
                          name="title"
                          value={chairmanData.title}
                          onChange={handleChairmanInputChange}
                          placeholder='e.g. "Steering Sahyadri with Community Trust"'
                        />

                        <FormTextarea
                          label="Message Quote / Statement Body *"
                          name="message"
                          value={chairmanData.message}
                          onChange={handleChairmanInputChange}
                          rows={4}
                          placeholder="Our journey in cooperative banking is rooted in mutual support..."
                        />

                        <div className="flex justify-end pt-3 border-t border-gray-100">
                          <button
                            type="submit"
                            disabled={submitLoading}
                            className="px-6 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase shadow-md flex items-center space-x-2"
                          >
                            <span>{submitLoading ? 'Saving...' : 'Update Chairman Info'}</span>
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* 2. CEO Form */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
                      <div className="border-b border-gray-100 pb-4 flex items-center justify-between">
                        <div>
                          <span className="text-[10px] font-bold text-[#052046] uppercase tracking-wider block">Executive Operations</span>
                          <h3 className="font-montserrat font-bold text-base text-[#052046]">CEO / General Manager Statement</h3>
                        </div>
                        <span className="text-[10px] font-mono bg-[#052046]/10 text-[#052046] px-2.5 py-1 rounded-md font-bold uppercase">CEO / GM</span>
                      </div>

                      <form onSubmit={handleCeoSubmit} className="space-y-4 text-xs text-gray-700">
                        {/* Avatar / Photo preview */}
                        <div className="flex items-center space-x-4 p-3.5 bg-[#F8FAFC] rounded-2xl border border-gray-150">
                          <div className="h-16 w-16 rounded-full bg-navy flex items-center justify-center text-golden overflow-hidden border-2 border-primary shrink-0">
                            {ceoData.imageUrl ? (
                              <img src={ceoData.imageUrl} alt="CEO" className="w-full h-full object-cover" />
                            ) : (
                              <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                                <rect width="100" height="100" fill="#0B1F3A" />
                                <circle cx="50" cy="40" r="18" fill="#D1D5DB" />
                                <path d="M25 80 C25 60, 75 60, 75 80 Z" fill="#F3F4F6" />
                              </svg>
                            )}
                          </div>
                          <div className="space-y-1 flex-1">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase">CEO / GM Photo / Portrait</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleCeoImageUpload}
                              className="text-[11px] text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-[10px] file:font-bold file:bg-[#052046] file:text-white hover:file:bg-[#0c346a] cursor-pointer"
                            />
                            {ceoData.imageUrl && (
                              <button
                                type="button"
                                onClick={() => setCeoData(prev => ({ ...prev, imageUrl: '' }))}
                                className="text-[10px] text-red-500 hover:underline block pt-0.5"
                              >
                                Remove custom photo
                              </button>
                            )}
                          </div>
                        </div>

                        <FormInput
                          label="CEO / GM Full Name *"
                          name="name"
                          value={ceoData.name}
                          onChange={handleCeoInputChange}
                          placeholder="e.g. Shri. Hanmant R. Ghadge"
                        />

                        <FormInput
                          label="Designation / Role Title *"
                          name="role"
                          value={ceoData.role}
                          onChange={handleCeoInputChange}
                          placeholder="e.g. CEO / General Manager"
                        />

                        <FormInput
                          label="Message Heading / Title *"
                          name="title"
                          value={ceoData.title}
                          onChange={handleCeoInputChange}
                          placeholder='e.g. "Innovating Core Banking Operations"'
                        />

                        <FormTextarea
                          label="Message Quote / Statement Body *"
                          name="message"
                          value={ceoData.message}
                          onChange={handleCeoInputChange}
                          rows={4}
                          placeholder="Digitalization is not just for mega banks. At Sahyadri, we believe our members deserve high-performance UPI transactions..."
                        />

                        <div className="flex justify-end pt-3 border-t border-gray-100">
                          <button
                            type="submit"
                            disabled={submitLoading}
                            className="px-6 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase shadow-md flex items-center space-x-2"
                          >
                            <span>{submitLoading ? 'Saving...' : 'Update CEO Info'}</span>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* ========== TOGGLE 2: BOARD OF DIRECTORS (CRUD) ========== */}
                {membersActiveTab === 'directors' && (
                  <div className="space-y-6">
                    {directorsList.length === 0 ? (
                      <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center space-y-3">
                        <Users className="h-12 w-12 text-gray-300 mx-auto" />
                        <h4 className="font-bold text-sm text-navy">No Board of Directors Added Yet</h4>
                        <p className="text-xs text-gray-400">Click "Add New Director" above to populate the governance board.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {directorsList.map((director) => (
                          <div
                            key={director.id}
                            className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 group"
                          >
                            <div className="space-y-3.5 text-center">
                              {/* Avatar */}
                              <div className="h-20 w-20 rounded-full bg-navy mx-auto flex items-center justify-center text-white overflow-hidden shadow-md border-2 border-golden/55 select-none">
                                {director.imageUrl ? (
                                  <img src={director.imageUrl} alt={director.name} className="w-full h-full object-cover" />
                                ) : (
                                  <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                                    <rect width="100" height="100" fill="#020C1A" />
                                    <circle cx="50" cy="40" r="18" fill="#B31825" />
                                    <path d="M25 80 C25 60, 75 60, 75 80 Z" fill="#E5E7EB" />
                                  </svg>
                                )}
                              </div>

                              <div>
                                <h4 className="font-montserrat font-bold text-base text-[#052046]">{director.name}</h4>
                                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-2.5 py-0.5 rounded-full inline-block mt-1">
                                  {director.role}
                                </span>
                              </div>

                              <p className="text-gray-500 text-xs leading-relaxed font-light line-clamp-3 px-2">
                                {director.description}
                              </p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              <span className="text-[10px] font-mono text-gray-400">Order #{director.order || 1}</span>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => loadDirectorForEditing(director)}
                                  className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                                  title="Edit Director"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDirectorDelete(director.id, director.name)}
                                  className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                                  title="Delete Director"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== TAB: CAREERS MANAGEMENT ==================== */}
        {activeTab === 'careers' && (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Human Resources & Recruitment</span>
                <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Careers & Job Postings Management</h1>
                <p className="text-gray-500 text-xs font-light">Manage open vacancies on the website and view received candidate applications in real-time.</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={openCreateJobModal}
                  className="px-5 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all flex items-center space-x-2 shadow-md"
                >
                  <PlusCircle className="h-4 w-4" />
                  <span>Create New Job</span>
                </button>
                <button onClick={fetchData} className="px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-bold rounded-xl text-xs uppercase tracking-wider transition-all">
                  Refresh
                </button>
              </div>
            </div>

            {/* 2-Toggle Navigation Switcher */}
            <div className="flex bg-white p-1 rounded-2xl border border-gray-200 max-w-lg shadow-sm">
              <button
                type="button"
                onClick={() => setCareersActiveTab('jobs')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 flex items-center justify-center space-x-2 ${
                  careersActiveTab === 'jobs' ? 'bg-[#052046] text-white shadow-sm' : 'text-gray-600 hover:text-[#052046]'
                }`}
              >
                <Briefcase className="h-4 w-4" />
                <span>Existing Job Postings ({careersList.length})</span>
              </button>

              <button
                type="button"
                onClick={() => setCareersActiveTab('applications')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 flex items-center justify-center space-x-2 ${
                  careersActiveTab === 'applications' ? 'bg-[#B31825] text-white shadow-sm' : 'text-gray-600 hover:text-[#B31825]'
                }`}
              >
                <Users className="h-4 w-4" />
                <span>Applications Received ({careerApplications.length})</span>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-gray-200">
                <Loader className="h-8 w-8 animate-spin text-[#B31825] mx-auto" />
                <p className="text-xs text-gray-400 mt-2">Loading career data...</p>
              </div>
            ) : (
              <>
                {/* ========== TOGGLE 1: EXISTING JOBS POSTED ========== */}
                {careersActiveTab === 'jobs' && (
                  <div>
                    {careersList.length === 0 ? (
                      <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center space-y-3">
                        <Briefcase className="h-12 w-12 text-gray-300 mx-auto" />
                        <h4 className="font-bold text-sm text-navy">No Job Postings Found</h4>
                        <p className="text-xs text-gray-400">Click "Create New Job" above to publish openings on the user website.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {careersList.map((job) => (
                          <div
                            key={job.id}
                            className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 relative group"
                          >
                            <div className="space-y-3">
                              <div className="flex justify-between items-start flex-wrap gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#052046]/10 text-[#052046] px-2.5 py-1 rounded-md">
                                  {job.department || 'Operations'}
                                </span>
                                {job.vacancies && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md">
                                    {job.vacancies}
                                  </span>
                                )}
                              </div>

                              <div>
                                <h3 className="font-montserrat font-bold text-base text-[#052046]">{job.title}</h3>
                                <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                                  <span className="flex items-center"><MapPin className="h-3 w-3 text-[#B31825] mr-1" />{job.location || 'Sangli'}</span>
                                  {job.type && <span className="flex items-center"><Clock className="h-3 w-3 text-gray-400 mr-1" />{job.type}</span>}
                                </div>
                              </div>

                              {job.salary && (
                                <span className="text-xs font-semibold text-golden-dark bg-golden/10 px-2.5 py-1 rounded-lg inline-block border border-golden/20">
                                  {job.salary}
                                </span>
                              )}

                              {job.description && (
                                <p className="text-xs text-gray-600 line-clamp-2 font-light">{job.description}</p>
                              )}

                              <div className="space-y-1.5 pt-2 border-t border-gray-100 text-[11px] text-gray-600">
                                {job.qualification && (
                                  <div className="flex items-start space-x-1.5">
                                    <GraduationCap className="h-3.5 w-3.5 text-[#B31825] shrink-0 mt-0.5" />
                                    <span><strong>Qual:</strong> {job.qualification}</span>
                                  </div>
                                )}
                                {job.experience && (
                                  <div className="flex items-start space-x-1.5">
                                    <Award className="h-3.5 w-3.5 text-[#B31825] shrink-0 mt-0.5" />
                                    <span><strong>Exp:</strong> {job.experience}</span>
                                  </div>
                                )}
                                {job.skills && (
                                  <div className="pt-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Skills:</span>
                                    <div className="flex flex-wrap gap-1">
                                      {job.skills.split(',').map((sk, idx) => (
                                        <span key={idx} className="text-[9px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded border border-gray-200">
                                          {sk.trim()}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex justify-end space-x-2 pt-3 border-t border-gray-100">
                              <button
                                onClick={() => loadJobForEditing(job)}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[10px] font-bold uppercase rounded-lg transition-colors border border-blue-200"
                              >
                                <Edit className="h-3.5 w-3.5" />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleJobDelete(job.id, job.title)}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-[10px] font-bold uppercase rounded-lg transition-colors border border-red-200"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                <span>Delete</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ========== TOGGLE 2: APPLICATIONS RECEIVED ========== */}
                {careersActiveTab === 'applications' && (
                  <div>
                    {careerApplications.length === 0 ? (
                      <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center space-y-3">
                        <Users className="h-12 w-12 text-gray-300 mx-auto" />
                        <h4 className="font-bold text-sm text-navy">No Applications Received Yet</h4>
                        <p className="text-xs text-gray-400">Applications submitted from the public Careers page will be listed here.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {careerApplications.map((app) => (
                          <div
                            key={app.id}
                            className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 group"
                          >
                            <div className="space-y-3">
                              <div className="flex justify-between items-start gap-2">
                                <div>
                                  <h4 className="font-montserrat font-bold text-sm text-[#052046]">{app.fullName || app.name || 'Candidate'}</h4>
                                  <span className="text-[10px] text-gray-400 font-mono block">{app.createdAt || 'N/A'}</span>
                                </div>
                                <span className={`text-[9px] font-bold uppercase px-2.5 py-1 rounded-full border ${getStatusBadge(app.status)}`}>
                                  {app.status || 'Applied'}
                                </span>
                              </div>

                              <div className="bg-[#F8FAFC] border border-gray-150 p-2.5 rounded-xl">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Position Applied:</span>
                                <span className="font-bold text-xs text-[#B31825]">{app.jobTitle || 'General Opening'}</span>
                              </div>

                              <div className="space-y-1 text-xs text-gray-600">
                                <div className="flex items-center space-x-2">
                                  <Phone className="h-3.5 w-3.5 text-[#B31825] shrink-0" />
                                  <a href={`tel:${app.phone}`} className="hover:underline text-navy font-medium">{app.phone || '—'}</a>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Mail className="h-3.5 w-3.5 text-[#B31825] shrink-0" />
                                  <a href={`mailto:${app.email}`} className="hover:underline text-navy font-medium truncate">{app.email || '—'}</a>
                                </div>
                                {app.experience && (
                                  <div className="flex items-center space-x-2 text-[11px]">
                                    <Award className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                    <span>Exp: {app.experience}</span>
                                  </div>
                                )}
                                {app.qualification && (
                                  <div className="flex items-center space-x-2 text-[11px]">
                                    <GraduationCap className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                    <span>Qual: {app.qualification}</span>
                                  </div>
                                )}
                              </div>

                              {app.coverNote && (
                                <div className="pt-1">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase block mb-0.5">Note:</span>
                                  <p className="text-xs text-gray-600 bg-gray-50 p-2.5 rounded-xl italic font-light line-clamp-3">
                                    "{app.coverNote}"
                                  </p>
                                </div>
                              )}
                            </div>

                            <div className="space-y-2 pt-3 border-t border-gray-100">
                              <div className="flex items-center justify-between gap-2">
                                <button
                                  onClick={() => setViewingResumeModal(app)}
                                  className="flex-1 inline-flex items-center justify-center space-x-1.5 py-2 bg-[#052046] hover:bg-[#0c346a] text-white text-[10px] font-bold uppercase rounded-xl transition-colors shadow-sm"
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                  <span>View Resume</span>
                                </button>
                                
                                <button
                                  onClick={() => handleApplicationDelete(app.id, app.fullName)}
                                  className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-colors border border-red-200"
                                  title="Delete Record"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>

                              {app.status !== 'Followed Up' && (
                                <button
                                  onClick={() => handleApplicationStatusChange(app.id, 'Followed Up')}
                                  className="w-full py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 text-[10px] font-bold uppercase rounded-xl transition-colors border border-amber-200 flex items-center justify-center space-x-1"
                                >
                                  <Phone className="h-3 w-3" />
                                  <span>Mark as Follow Up</span>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== TAB: MANAGE NOTICES ==================== */}
        {activeTab === 'manage-notices' && (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Regulatory & Board Broadcasts</span>
                <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Manage Notices & News Portal</h1>
                <p className="text-gray-500 text-xs font-light">Publish categorized news articles and upload official PDF circulars for member download.</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={openCreateNoticeModal}
                  className="px-5 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all flex items-center space-x-2 shadow-md"
                >
                  <PlusCircle className="h-4 w-4" />
                  <span>Add Notice / News</span>
                </button>
                <button onClick={fetchData} className="px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-bold rounded-xl text-xs uppercase tracking-wider transition-all">
                  Refresh
                </button>
              </div>
            </div>

            <div className="flex bg-white p-1 rounded-2xl border border-gray-200 max-w-lg shadow-sm">
              <button
                type="button"
                onClick={() => setNoticesActiveTab('news')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 flex items-center justify-center space-x-2 ${
                  noticesActiveTab === 'news' ? 'bg-[#052046] text-white shadow-sm' : 'text-gray-600 hover:text-[#052046]'
                }`}
              >
                <Megaphone className="h-4 w-4" />
                <span>Categorized News ({newsList.length})</span>
              </button>

              <button
                type="button"
                onClick={() => setNoticesActiveTab('pdfNotices')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 flex items-center justify-center space-x-2 ${
                  noticesActiveTab === 'pdfNotices' ? 'bg-[#B31825] text-white shadow-sm' : 'text-gray-600 hover:text-[#B31825]'
                }`}
              >
                <FileText className="h-4 w-4" />
                <span>Official PDF Notices ({pdfNoticesList.length})</span>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-gray-200">
                <Loader className="h-8 w-8 animate-spin text-[#B31825] mx-auto" />
                <p className="text-xs text-gray-400 mt-2">Loading notice records...</p>
              </div>
            ) : (
              <>
                {noticesActiveTab === 'news' && (
                  <div>
                    {newsList.length === 0 ? (
                      <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center space-y-3">
                        <Megaphone className="h-12 w-12 text-gray-300 mx-auto" />
                        <h4 className="font-bold text-sm text-navy">No News Articles Found</h4>
                        <p className="text-xs text-gray-400">Click "Add Notice / News" to create an announcement.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {newsList.map((item) => (
                          <div
                            key={item.id}
                            className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 group"
                          >
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-[10px] bg-[#B31825]/10 text-[#B31825] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider">
                                  {item.category || 'Announcements'}
                                </span>
                                <span className="text-xs text-gray-400 font-poppins flex items-center">
                                  <Calendar className="h-3.5 w-3.5 mr-1" />
                                  {item.date}
                                </span>
                              </div>

                              <div>
                                <h3 className="font-montserrat font-bold text-base text-[#052046]">
                                  {item.isCircular && (
                                    <span className="text-[9px] bg-red-100 text-red-600 font-extrabold px-2 py-0.5 rounded mr-2 uppercase">
                                      Circular
                                    </span>
                                  )}
                                  {item.title}
                                </h3>
                              </div>

                              {item.summary && (
                                <div className="text-xs text-gray-700 border-l-3 border-[#B31825] pl-3 py-1 bg-[#F8FAFC] rounded-r-lg font-medium line-clamp-2">
                                  {item.summary}
                                </div>
                              )}

                              <p className="text-xs text-gray-500 font-light line-clamp-3">
                                {item.content}
                              </p>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              <button
                                onClick={() => setViewingNoticeModal(item)}
                                className="text-xs text-[#052046] font-bold uppercase hover:underline flex items-center space-x-1"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                <span>Preview Modal</span>
                              </button>

                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => loadCategoryNewsForEditing(item)}
                                  className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handleCategoryNewsDelete(item.id, item.title)}
                                  className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {noticesActiveTab === 'pdfNotices' && (
                  <div>
                    {pdfNoticesList.length === 0 ? (
                      <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center space-y-3">
                        <FileText className="h-12 w-12 text-gray-300 mx-auto" />
                        <h4 className="font-bold text-sm text-navy">No Official PDF Notices Uploaded</h4>
                        <p className="text-xs text-gray-400">Click "Add Notice / News" and select the PDF tab to upload downloadable documents.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {pdfNoticesList.map((notice) => (
                          <div
                            key={notice.id}
                            className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 group"
                          >
                            <div className="space-y-3">
                              <div className="flex justify-between items-start flex-wrap gap-2">
                                <span className="text-[10px] font-mono font-bold bg-[#052046] text-white px-2.5 py-1 rounded-lg">
                                  {notice.noticeNo || 'SHYD/CIR/2026'}
                                </span>
                                <span className="text-xs text-gray-400 font-poppins flex items-center">
                                  <Calendar className="h-3.5 w-3.5 mr-1 text-[#B31825]" />
                                  {notice.date}
                                </span>
                              </div>

                              <div>
                                <span className="text-[10px] font-bold text-golden-dark uppercase tracking-wider block">
                                  {notice.department || notice.category || 'General Administration'}
                                </span>
                                <h3 className="font-montserrat font-bold text-base text-[#052046]">{notice.title}</h3>
                              </div>

                              <p className="text-xs text-gray-600 font-light line-clamp-3">
                                {notice.description}
                              </p>

                              <div className="bg-[#F8FAFC] border border-gray-150 p-2.5 rounded-xl flex items-center justify-between text-xs">
                                <span className="text-gray-500 font-mono truncate flex items-center">
                                  <FileText className="h-3.5 w-3.5 mr-1 text-gray-400 shrink-0" />
                                  {notice.pdfName || 'Official_Document.pdf'}
                                </span>
                                {notice.fileSize && <span className="text-gray-400 text-[10px]">{notice.fileSize}</span>}
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              {notice.pdfUrl ? (
                                <a
                                  href={notice.pdfUrl}
                                  download={notice.pdfName || `${notice.title}.pdf`}
                                  className="inline-flex items-center space-x-1 text-xs text-[#052046] font-bold uppercase hover:underline"
                                >
                                  <Download className="h-3.5 w-3.5" />
                                  <span>Download PDF</span>
                                </a>
                              ) : (
                                <span className="text-[10px] text-gray-400 italic">No PDF File Attached</span>
                              )}

                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => loadPdfNoticeForEditing(notice)}
                                  className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handlePdfNoticeDelete(notice.id, notice.title)}
                                  className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== TAB: DEPOSIT ENQUIRIES ==================== */}
        {activeTab === 'deposit-enquiries' && (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Investor Submissions</span>
                <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Deposit Scheme Enquiries</h1>
                <p className="text-gray-500 text-xs font-light">Direct applications submitted via the public Fixed Deposit & Recurring Deposit pages.</p>
              </div>
              <button onClick={fetchData} className="px-5 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all">Refresh</button>
            </div>
            {loading ? (
              <div className="text-center py-20"><Loader className="h-8 w-8 animate-spin text-[#B31825] mx-auto" /></div>
            ) : depositEnquiries.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center"><ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-400 text-sm">No deposit enquiries received yet.</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {depositEnquiries.map(enq => (
                  <EnquiryCard key={enq.id} enq={enq} collectionName="deposit_enquiries" fields={[
                    { label: 'Scheme', key: 'schemeName', icon: Landmark },
                    { label: 'Amount', key: 'depositAmount', icon: IndianRupee },
                    { label: 'Phone', key: 'phone', icon: Phone },
                    { label: 'City', key: 'city', icon: MapPin },
                  ]} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: LOAN ENQUIRIES ==================== */}
        {activeTab === 'loan-enquiries' && (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Borrower Submissions</span>
                <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Loan Application Enquiries</h1>
                <p className="text-gray-500 text-xs font-light">Gold, agriculture, MSME, and personal credit requests captured from public website.</p>
              </div>
              <button onClick={fetchData} className="px-5 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all">Refresh</button>
            </div>
            {loading ? (
              <div className="text-center py-20"><Loader className="h-8 w-8 animate-spin text-[#B31825] mx-auto" /></div>
            ) : loanEnquiries.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center"><FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-400 text-sm">No loan applications received yet.</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {loanEnquiries.map(enq => (
                  <EnquiryCard key={enq.id} enq={enq} collectionName="loan_enquiries" fields={[
                    { label: 'Product', key: 'productName', icon: CreditCard },
                    { label: 'Amount', key: 'loanAmount', icon: IndianRupee },
                    { label: 'Phone', key: 'phone', icon: Phone },
                    { label: 'City', key: 'city', icon: MapPin },
                  ]} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: CONTACT ==================== */}
        {activeTab === 'contact' && (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Member Interactions</span>
                <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Contact Desk Submissions</h1>
                <p className="text-gray-500 text-xs font-light">Submissions received via Quick Enquiry widget, Complaint form, and Member Feedback.</p>
              </div>
              <button onClick={fetchData} className="px-5 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all">Refresh</button>
            </div>

            <div className="flex bg-white p-1 rounded-2xl border border-gray-200 max-w-xl justify-between gap-1 select-none shadow-sm">
              <button
                type="button"
                onClick={() => setContactActiveTab('enquiry')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 ${contactActiveTab === 'enquiry' ? 'bg-[#052046] text-white shadow-sm' : 'text-gray-500 hover:text-[#052046]'}`}
              >
                Quick Enquiry ({contactEnquiries.length})
              </button>
              <button
                type="button"
                onClick={() => setContactActiveTab('complaint')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 ${contactActiveTab === 'complaint' ? 'bg-[#B31825] text-white shadow-sm' : 'text-gray-500 hover:text-[#B31825]'}`}
              >
                File Complaint ({contactComplaints.length})
              </button>
              <button
                type="button"
                onClick={() => setContactActiveTab('feedback')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 ${contactActiveTab === 'feedback' ? 'bg-[#00A86B] text-white shadow-sm' : 'text-gray-500 hover:text-[#00A86B]'}`}
              >
                Feedback ({contactFeedbacks.length})
              </button>
            </div>

            {loading ? (
              <div className="text-center py-20"><Loader className="h-8 w-8 animate-spin text-[#B31825] mx-auto" /></div>
            ) : (
              <>
                {contactActiveTab === 'enquiry' && (
                  contactEnquiries.length === 0 ? (
                    <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center"><Phone className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-400 text-sm">No quick enquiries received yet.</p></div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                      {contactEnquiries.map(enq => (
                        <ContactEnquiryCard key={enq.id} enq={enq} collectionName="enquiries" />
                      ))}
                    </div>
                  )
                )}

                {contactActiveTab === 'complaint' && (
                  contactComplaints.length === 0 ? (
                    <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center"><AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-400 text-sm">No complaints received yet.</p></div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                      {contactComplaints.map(enq => (
                        <ContactEnquiryCard key={enq.id} enq={enq} collectionName="complaints" />
                      ))}
                    </div>
                  )
                )}

                {contactActiveTab === 'feedback' && (
                  contactFeedbacks.length === 0 ? (
                    <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center"><Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-400 text-sm">No feedback received yet.</p></div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                      {contactFeedbacks.map(enq => (
                        <ContactEnquiryCard key={enq.id} enq={enq} collectionName="feedbacks" />
                      ))}
                    </div>
                  )
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== TAB: ACCOUNT ENQUIRIES ==================== */}
        {activeTab === 'account-enquiries' && (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider">Customer Submissions</span>
                <h1 className="font-montserrat font-extrabold text-2xl text-[#052046]">Account Application Enquiries</h1>
                <p className="text-gray-500 text-xs font-light">Submissions made through the Savings and Current Account pages.</p>
              </div>
              <button onClick={fetchData} className="px-5 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all">Refresh</button>
            </div>

            <div className="flex bg-white p-1 rounded-2xl border border-gray-200 max-w-xl justify-between gap-1 select-none shadow-sm">
              <button
                type="button"
                onClick={() => setAccountActiveTab('savings')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 ${accountActiveTab === 'savings' ? 'bg-[#052046] text-white shadow-sm' : 'text-gray-500 hover:text-[#052046]'}`}
              >
                Savings Account ({accountEnquiries.filter(e => e.accountType === 'Savings Account').length})
              </button>
              <button
                type="button"
                onClick={() => setAccountActiveTab('current')}
                className={`flex-1 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 ${accountActiveTab === 'current' ? 'bg-[#052046] text-white shadow-sm' : 'text-gray-500 hover:text-[#052046]'}`}
              >
                Current Account ({accountEnquiries.filter(e => e.accountType === 'Current Account').length})
              </button>
            </div>

            {loading ? (
              <div className="text-center py-20"><Loader className="h-8 w-8 animate-spin text-[#B31825] mx-auto" /></div>
            ) : (
              <>
                {accountActiveTab === 'savings' && (
                  accountEnquiries.filter(e => e.accountType === 'Savings Account').length === 0 ? (
                    <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center"><Users className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-400 text-sm">No savings account enquiries received yet.</p></div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                      {accountEnquiries.filter(e => e.accountType === 'Savings Account').map(enq => (
                        <AccountEnquiryCard key={enq.id} enq={enq} collectionName="account_enquiries" />
                      ))}
                    </div>
                  )
                )}

                {accountActiveTab === 'current' && (
                  accountEnquiries.filter(e => e.accountType === 'Current Account').length === 0 ? (
                    <div className="bg-white border-2 border-dashed border-gray-200 rounded-3xl p-16 text-center"><Users className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-400 text-sm">No current account enquiries received yet.</p></div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                      {accountEnquiries.filter(e => e.accountType === 'Current Account').map(enq => (
                        <AccountEnquiryCard key={enq.id} enq={enq} collectionName="account_enquiries" />
                      ))}
                    </div>
                  )
                )}
              </>
            )}
          </div>
        )}

      </main>

      {/* ========================================================================= */}
      {/* ========================= MODALS & OVERLAYS ============================= */}
      {/* ========================================================================= */}

      {/* 1. ADD / EDIT BOARD OF DIRECTOR MODAL */}
      {directorModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden border border-gray-100 animate-fadeIn">
            <div className="bg-[#052046] text-white p-6 relative">
              <div className="flex items-center space-x-2">
                <UserCheck className="h-5 w-5 text-golden" />
                <h3 className="font-montserrat font-extrabold text-lg">
                  {directorFormMode === 'add' ? 'Add Board of Director' : 'Update Director Information'}
                </h3>
              </div>
              <p className="text-gray-300 text-xs mt-1 font-light">Add or update board members visible on the public About page.</p>
              <button onClick={() => setDirectorModalOpen(false)} className="absolute top-6 right-6 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleDirectorFormSubmit} className="p-6 sm:p-8 space-y-4 max-h-[75vh] overflow-y-auto text-xs text-gray-700">
              {/* Photo Upload preview */}
              <div className="flex items-center space-x-4 p-3 bg-[#F8FAFC] rounded-2xl border border-gray-150">
                <div className="h-14 w-14 rounded-full bg-navy flex items-center justify-center text-white overflow-hidden border-2 border-golden shrink-0">
                  {directorFormData.imageUrl ? (
                    <img src={directorFormData.imageUrl} alt="Director" className="w-full h-full object-cover" />
                  ) : (
                    <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                      <rect width="100" height="100" fill="#020C1A" />
                      <circle cx="50" cy="40" r="18" fill="#B31825" />
                      <path d="M25 80 C25 60, 75 60, 75 80 Z" fill="#E5E7EB" />
                    </svg>
                  )}
                </div>
                <div className="space-y-1 flex-1">
                  <label className="block text-[10px] font-bold text-gray-500 uppercase">Director Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleDirectorImageUpload}
                    className="text-[11px] text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-[10px] file:font-bold file:bg-[#052046] file:text-white hover:file:bg-[#0c346a] cursor-pointer"
                  />
                  {directorFormData.imageUrl && (
                    <button
                      type="button"
                      onClick={() => setDirectorFormData(prev => ({ ...prev, imageUrl: '' }))}
                      className="text-[10px] text-red-500 hover:underline block pt-0.5"
                    >
                      Remove photo
                    </button>
                  )}
                </div>
              </div>

              <FormInput
                label="Full Name *"
                name="name"
                value={directorFormData.name}
                onChange={handleDirectorInputChange}
                placeholder="e.g. Smt. Vasundhara R. Mohite"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600">Role / Designation *</label>
                  <input
                    type="text"
                    name="role"
                    value={directorFormData.role}
                    onChange={handleDirectorInputChange}
                    placeholder="e.g. Vice Chairman / Director (Finance)"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B31825]/20 focus:border-[#B31825] text-xs transition-colors"
                  />
                </div>

                <FormInput
                  label="Display Order (Sort)"
                  name="order"
                  type="number"
                  value={directorFormData.order}
                  onChange={handleDirectorInputChange}
                  placeholder="1, 2, 3..."
                  hint="Order in which card appears on About page"
                />
              </div>

              <FormTextarea
                label="Bio / Background Description *"
                name="description"
                value={directorFormData.description}
                onChange={handleDirectorInputChange}
                rows={3}
                placeholder="Ex-Cooperative Leader with 35+ years of banking experience. Guiding Sahyadri toward digital growth..."
              />

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setDirectorModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold uppercase text-xs hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="px-6 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase shadow-md flex items-center space-x-2"
                >
                  <span>{submitLoading ? 'Saving...' : (directorFormMode === 'add' ? 'Save Director' : 'Update Director')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. CREATE / EDIT JOB VACANCY MODAL */}
      {jobModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden border border-gray-100 animate-fadeIn">
            <div className="bg-[#052046] text-white p-6 relative">
              <div className="flex items-center space-x-2">
                <Briefcase className="h-5 w-5 text-golden" />
                <h3 className="font-montserrat font-extrabold text-lg">
                  {jobFormMode === 'add' ? 'Post New Career Opportunity' : 'Update Job Vacancy'}
                </h3>
              </div>
              <p className="text-gray-300 text-xs mt-1 font-light">Configure job specifications to be published on the website.</p>
              <button onClick={() => setJobModalOpen(false)} className="absolute top-6 right-6 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleJobFormSubmit} className="p-6 sm:p-8 space-y-4 max-h-[75vh] overflow-y-auto text-xs text-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput
                  label="Job Title *"
                  name="title"
                  value={jobFormData.title}
                  onChange={handleJobInputChange}
                  placeholder="e.g. Branch Manager"
                />
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600">Department *</label>
                  <select
                    name="department"
                    value={jobFormData.department}
                    onChange={handleJobInputChange}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B31825]/20 focus:border-[#B31825] text-xs transition-colors bg-white"
                  >
                    <option value="Branch Operations">Branch Operations</option>
                    <option value="Credit & Loans">Credit & Loans</option>
                    <option value="Customer Service">Customer Service</option>
                    <option value="IT & Digital Banking">IT & Digital Banking</option>
                    <option value="Audit & Compliance">Audit & Compliance</option>
                    <option value="Accounts & Finance">Accounts & Finance</option>
                    <option value="HR & Administration">HR & Administration</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput
                  label="Location / Branch *"
                  name="location"
                  value={jobFormData.location}
                  onChange={handleJobInputChange}
                  placeholder="e.g. Sangli Head Office / Mumbai"
                />
                <FormInput
                  label="Vacancies Available *"
                  name="vacancies"
                  value={jobFormData.vacancies}
                  onChange={handleJobInputChange}
                  placeholder="e.g. 3 Posts"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput
                  label="Required Qualification *"
                  name="qualification"
                  value={jobFormData.qualification}
                  onChange={handleJobInputChange}
                  placeholder="e.g. B.Com / M.Com / MBA in Finance"
                />
                <FormInput
                  label="Experience Needed *"
                  name="experience"
                  value={jobFormData.experience}
                  onChange={handleJobInputChange}
                  placeholder="e.g. 2-4 Years in Banking"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput
                  label="Salary / Package"
                  name="salary"
                  value={jobFormData.salary}
                  onChange={handleJobInputChange}
                  placeholder="e.g. ₹35,000 - ₹45,000 / month"
                />
                <FormInput
                  label="Employment Type"
                  name="type"
                  value={jobFormData.type}
                  onChange={handleJobInputChange}
                  placeholder="e.g. Full-Time / Permanent"
                />
              </div>

              <FormInput
                label="Skills Required (Comma-separated) *"
                name="skills"
                value={jobFormData.skills}
                onChange={handleJobInputChange}
                placeholder="e.g. Cash Handling, Net Banking, Tally, Communication, KYC"
                hint="Enter skills separated by commas (will be displayed as tags)"
              />

              <FormTextarea
                label="Job Description / Responsibilities *"
                name="description"
                value={jobFormData.description}
                onChange={handleJobInputChange}
                rows={3}
                placeholder="Describe key duties, customer interactions, and daily operational targets..."
              />

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setJobModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold uppercase text-xs hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="px-6 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase shadow-md flex items-center space-x-2"
                >
                  <span>{submitLoading ? 'Saving...' : (jobFormMode === 'add' ? 'Publish Job' : 'Update Job')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. MANAGE NOTICES (2-IN-1 TOGGLE FORM MODAL) */}
      {noticeModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden border border-gray-100 animate-fadeIn">
            <div className="bg-[#052046] text-white p-6 relative">
              <div className="flex items-center space-x-2">
                <Bell className="h-5 w-5 text-golden" />
                <h3 className="font-montserrat font-extrabold text-lg">
                  {noticeFormToggle === 'category'
                    ? (categoryFormMode === 'add' ? 'Create Categorized News Article' : 'Update News Article')
                    : (pdfNoticeFormMode === 'add' ? 'Upload Official Notice / Circular (PDF)' : 'Update Official Notice')
                  }
                </h3>
              </div>
              <p className="text-gray-300 text-xs mt-1 font-light">Publish regulatory circulars and notices to the public portal.</p>
              <button onClick={() => setNoticeModalOpen(false)} className="absolute top-6 right-6 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* In-Modal Toggle Switcher */}
            <div className="p-4 bg-[#F8FAFC] border-b border-gray-200">
              <div className="flex bg-white p-1 rounded-xl border border-gray-200 shadow-sm">
                <button
                  type="button"
                  onClick={() => setNoticeFormToggle('category')}
                  className={`flex-1 py-2 rounded-lg font-montserrat font-bold text-xs transition-all ${
                    noticeFormToggle === 'category'
                      ? 'bg-[#B31825] text-white shadow-sm'
                      : 'text-gray-600 hover:text-[#B31825]'
                  }`}
                >
                  1. News & Category Article
                </button>
                <button
                  type="button"
                  onClick={() => setNoticeFormToggle('pdf')}
                  className={`flex-1 py-2 rounded-lg font-montserrat font-bold text-xs transition-all ${
                    noticeFormToggle === 'pdf'
                      ? 'bg-[#052046] text-white shadow-sm'
                      : 'text-gray-600 hover:text-[#052046]'
                  }`}
                >
                  2. Official PDF Notice Section
                </button>
              </div>
            </div>

            {noticeFormToggle === 'category' && (
              <form onSubmit={handleCategoryNewsSubmit} className="p-6 sm:p-8 space-y-4 max-h-[65vh] overflow-y-auto text-xs text-gray-700">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600">Category Section *</label>
                    <select
                      name="category"
                      value={categoryFormData.category}
                      onChange={handleCategoryInputChange}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B31825]/20 focus:border-[#B31825] text-xs transition-colors bg-white font-semibold"
                    >
                      <option value="Announcements">Announcements</option>
                      <option value="New Schemes">New Schemes</option>
                      <option value="Cyber Security">Cyber Security</option>
                      <option value="Financial Awareness">Financial Awareness</option>
                    </select>
                  </div>

                  <FormInput
                    label="Published Date *"
                    name="date"
                    type="date"
                    value={categoryFormData.date}
                    onChange={handleCategoryInputChange}
                  />
                </div>

                <FormInput
                  label="Article / Notice Title *"
                  name="title"
                  value={categoryFormData.title}
                  onChange={handleCategoryInputChange}
                  placeholder="e.g. Sahyadri Urban Launches Secure Member Portal"
                />

                <FormTextarea
                  label="Highlight / Key Summary Quote *"
                  name="summary"
                  value={categoryFormData.summary}
                  onChange={handleCategoryInputChange}
                  rows={2}
                  placeholder="Members can check account balances, download statement files, and request funds transfer details via the new secure portal."
                  hint="This appears in the red-bordered quote box in the modal popup"
                />

                <FormTextarea
                  label="Full Details / Body Content *"
                  name="content"
                  value={categoryFormData.content}
                  onChange={handleCategoryInputChange}
                  rows={4}
                  placeholder="We are thrilled to announce the launch of our new online Member Portal. Access it directly from our website using your registered credentials..."
                />

                <div className="flex items-center space-x-2 pt-1">
                  <input
                    type="checkbox"
                    id="isCircular"
                    name="isCircular"
                    checked={categoryFormData.isCircular}
                    onChange={handleCategoryInputChange}
                    className="h-4 w-4 text-[#B31825] rounded border-gray-300 focus:ring-[#B31825]"
                  />
                  <label htmlFor="isCircular" className="text-xs font-semibold text-gray-700 cursor-pointer">
                    Mark as Official Circular (Shows red "Circular" badge)
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setNoticeModalOpen(false)}
                    className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold uppercase text-xs hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="px-6 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase shadow-md"
                  >
                    <span>{submitLoading ? 'Saving...' : (categoryFormMode === 'add' ? 'Publish Article' : 'Update Article')}</span>
                  </button>
                </div>
              </form>
            )}

            {noticeFormToggle === 'pdf' && (
              <form onSubmit={handlePdfNoticeSubmit} className="p-6 sm:p-8 space-y-4 max-h-[65vh] overflow-y-auto text-xs text-gray-700">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormInput
                    label="Notice Reference Number *"
                    name="noticeNo"
                    value={pdfNoticeFormData.noticeNo}
                    onChange={handlePdfNoticeInputChange}
                    placeholder="e.g. SHYD/AGM/2026-27/01"
                  />

                  <FormInput
                    label="Published Date *"
                    name="date"
                    type="date"
                    value={pdfNoticeFormData.date}
                    onChange={handlePdfNoticeInputChange}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormInput
                    label="Notice Title *"
                    name="title"
                    value={pdfNoticeFormData.title}
                    onChange={handlePdfNoticeInputChange}
                    placeholder="e.g. 42nd Annual General Body Meeting (AGM) Notice & Agenda"
                  />

                  <FormInput
                    label="Department / Category"
                    name="department"
                    value={pdfNoticeFormData.department}
                    onChange={handlePdfNoticeInputChange}
                    placeholder="e.g. Board Administration / Audit / Operations"
                  />
                </div>

                <FormTextarea
                  label="Notice Summary / Brief Description *"
                  name="description"
                  value={pdfNoticeFormData.description}
                  onChange={handlePdfNoticeInputChange}
                  rows={3}
                  placeholder="Official notice to all voting members regarding the 42nd AGM scheduled at Sangli Headquarters..."
                />

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Attach Notice PDF Document *
                  </label>
                  {!pdfNoticeFormData.pdfUrl ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-4 text-center hover:border-[#052046] transition-colors bg-[#F8FAFC]">
                      <input
                        id="notice-pdf-input"
                        type="file"
                        accept=".pdf"
                        onChange={handleNoticePdfUpload}
                        className="hidden"
                      />
                      <label htmlFor="notice-pdf-input" className="cursor-pointer flex flex-col items-center justify-center space-y-1">
                        <UploadCloud className="h-6 w-6 text-[#052046]" />
                        <span className="text-xs font-bold text-navy">Click to select Official Notice PDF</span>
                        <span className="text-[10px] text-gray-400">PDF documents only (Max 1.5MB)</span>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-2xl">
                      <div className="flex items-center space-x-2.5 overflow-hidden">
                        <FileText className="h-5 w-5 text-[#052046] shrink-0" />
                        <div className="truncate">
                          <p className="text-xs font-bold text-navy truncate">{pdfNoticeFormData.pdfName}</p>
                          <span className="text-[10px] text-gray-500">{pdfNoticeFormData.fileSize}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPdfNoticeFormData(prev => ({ ...prev, pdfUrl: '', pdfName: '', fileSize: '' }))}
                        className="p-1 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                        title="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setNoticeModalOpen(false)}
                    className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold uppercase text-xs hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="px-6 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase shadow-md"
                  >
                    <span>{submitLoading ? 'Saving...' : (pdfNoticeFormMode === 'add' ? 'Upload Notice' : 'Update Notice')}</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* 4. RESUME VIEWER MODAL */}
      {viewingResumeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden border border-gray-100 animate-fadeIn">
            <div className="bg-[#052046] text-white p-6 relative">
              <span className="text-[10px] font-bold uppercase tracking-wider bg-golden text-navy px-2.5 py-0.5 rounded font-mono">
                {viewingResumeModal.applicationId || 'APP'}
              </span>
              <h3 className="font-montserrat font-extrabold text-xl mt-1">
                {viewingResumeModal.fullName || 'Candidate Profile'}
              </h3>
              <p className="text-gray-300 text-xs font-light">
                Position: <strong className="text-white">{viewingResumeModal.jobTitle}</strong>
              </p>
              <button onClick={() => setViewingResumeModal(null)} className="absolute top-6 right-6 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 sm:p-8 space-y-4 max-h-[65vh] overflow-y-auto text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-[#F8FAFC] p-4 rounded-2xl border border-gray-200">
                <div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase block">Phone Number</span>
                  <a href={`tel:${viewingResumeModal.phone}`} className="font-bold text-sm text-[#B31825]">{viewingResumeModal.phone || 'N/A'}</a>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase block">Email Address</span>
                  <a href={`mailto:${viewingResumeModal.email}`} className="font-bold text-sm text-navy">{viewingResumeModal.email || 'N/A'}</a>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase block">Total Experience</span>
                  <span className="font-semibold text-gray-700">{viewingResumeModal.experience || 'Not specified'}</span>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase block">Qualification</span>
                  <span className="font-semibold text-gray-700">{viewingResumeModal.qualification || 'Not specified'}</span>
                </div>
              </div>

              {viewingResumeModal.coverNote && (
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-gray-400 uppercase">Cover Note / Candidate Remarks</span>
                  <p className="text-gray-700 bg-gray-50 p-3.5 rounded-xl border border-gray-150 whitespace-pre-line leading-relaxed font-light">
                    {viewingResumeModal.coverNote}
                  </p>
                </div>
              )}

              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-[10px] font-bold text-gray-400 uppercase block">Attached Resume Document</span>
                {viewingResumeModal.resumeUrl ? (
                  <div className="bg-[#052046]/5 border border-[#052046]/10 p-4 rounded-2xl flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-8 w-8 text-[#B31825]" />
                      <div>
                        <h4 className="font-bold text-sm text-navy">{viewingResumeModal.resumeName || 'Resume_Document.pdf'}</h4>
                        <span className="text-[10px] text-gray-500">{viewingResumeModal.resumeSize || 'Uploaded file'}</span>
                      </div>
                    </div>
                    <a
                      href={viewingResumeModal.resumeUrl}
                      download={viewingResumeModal.resumeName || 'Resume.pdf'}
                      className="px-4 py-2 bg-[#B31825] hover:bg-[#8e131d] text-white font-bold rounded-xl text-xs uppercase flex items-center space-x-1.5 shadow-md"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </a>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 text-center text-gray-400">
                    <FileText className="h-6 w-6 mx-auto mb-1 text-gray-300" />
                    <span>No digital file uploaded. (Candidate provided form credentials above).</span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-[#F8FAFC] border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={() => setViewingResumeModal(null)}
                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold rounded-xl uppercase transition-colors"
              >
                Close
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    handleApplicationStatusChange(viewingResumeModal.id, 'Followed Up');
                    setViewingResumeModal(null);
                  }}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl uppercase shadow-sm"
                >
                  Mark Followed Up
                </button>
                <button
                  onClick={() => {
                    handleApplicationStatusChange(viewingResumeModal.id, 'Shortlisted');
                    setViewingResumeModal(null);
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl uppercase shadow-sm"
                >
                  Shortlist Candidate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 5. NOTICE PREVIEW MODAL */}
      {viewingNoticeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden border border-gray-100 relative">
            <button onClick={() => setViewingNoticeModal(null)} className="absolute top-5 right-5 p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white z-20 border border-white/10">
              <X className="h-5 w-5" />
            </button>

            <div className="bg-[#052046] text-white p-6 sm:p-8 space-y-3 relative overflow-hidden">
              <span className="text-[10px] bg-[#B31825] text-white font-extrabold px-3 py-1 rounded-full uppercase tracking-wider inline-block">
                {viewingNoticeModal.category || 'Announcements'}
              </span>
              <h2 className="font-montserrat font-extrabold text-xl sm:text-2xl text-white pr-8">
                {viewingNoticeModal.title}
              </h2>
              <div className="flex items-center text-xs text-gray-300 font-poppins pt-1">
                <Calendar className="h-4 w-4 mr-1.5 text-golden" />
                <span>Published on {viewingNoticeModal.date}</span>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-5 max-h-[360px] overflow-y-auto">
              {viewingNoticeModal.summary && (
                <div className="text-xs sm:text-sm text-gray-700 font-semibold leading-relaxed border-l-4 border-[#B31825] pl-4 bg-[#F8FAFC] py-3 rounded-r-xl border border-gray-100">
                  {viewingNoticeModal.summary}
                </div>
              )}
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed whitespace-pre-line font-light">
                {viewingNoticeModal.content || viewingNoticeModal.description}
              </p>
            </div>

            <div className="p-5 bg-[#F8FAFC] border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setViewingNoticeModal(null)}
                className="px-6 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white text-xs font-bold rounded-xl uppercase tracking-wider shadow-md"
              >
                Close Notice
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
